import React, { useState, useEffect, createContext, useContext, useRef, useMemo } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import proj4 from "proj4";

// Google Maps API Key - can be overridden via environment variable
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyApXOY3p0B_LN8qLOL_l52ojn-hmUOQMdA";

// Google Maps Map ID - Required for Advanced Markers
// Create one at: https://console.cloud.google.com/google/maps-apis/studio/maps
// Format: "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" (32 characters)
const GOOGLE_MAPS_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || null;

// Define Belgian Lambert 72 projection (EPSG:31370)
proj4.defs("EPSG:31370", "+proj=lcc +lat_1=51.16666723333333 +lat_2=49.8333339 +lat_0=90 +lon_0=4.367486666666666 +x_0=150000.013 +y_0=5400088.438 +ellps=intl +towgs84=-106.8686,52.2978,-103.7239,0.3366,-0.457,1.8422,-1.2747 +units=m +no_defs");
proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Separator } from "./components/ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./components/ui/sheet";
import { Label } from "./components/ui/label";
import { Switch } from "./components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Checkbox } from "./components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./components/ui/dropdown-menu";
import AdminDashboard from "./components/AdminDashboard";
import ProjectsModule from "./components/ProjectsModule";
import OOWoningenModule from "./components/OOWoningenModule";
import VacantSpacesModule from "./components/VacantSpacesModule";
import AdminModuleAccess from "./components/AdminModuleAccess";
import logoImage from "./assets/images/logo.png";
import { 
  Search, 
  Mail, 
  MapPin, 
  Calendar, 
  Building, 
  User, 
  Phone, 
  Star,
  LogOut,
  Users,
  Eye,
  Briefcase,
  CheckCircle,
  XCircle,
  Clock,
  Archive,
  Send,
  Download,
  Upload,
  FileText,
  Zap,
  Target,
  MailOpen,
  Package,
  Plus,
  ArrowRight,
  RefreshCw,
  X,
  ExternalLink,
  ChevronDown,
  UserCircle,
  Edit,
  Trash2,
  Tag,
  Settings
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MODULE_ALIASES = {
  verguningen: 'permits',
  vergunningen: 'permits',
  'oo-woningen': 'oo_woningen',
  leegstand: 'vacant_spaces',
};

const normalizeModuleId = (moduleId) => {
  if (typeof moduleId !== 'string') {
    return moduleId;
  }
  const normalized = moduleId.trim();
  return MODULE_ALIASES[normalized] || normalized;
};

const MODULE_ROUTE_ORDER = [
  { moduleIds: ['permits'], path: '/dashboard' },
  { moduleIds: ['permits'], path: '/prospectie' },
  { moduleIds: ['oo_woningen'], path: '/oo-woningen' },
  { moduleIds: ['vacant_spaces'], path: '/vacant-spaces' },
];

const NO_MODULE_FALLBACK_ROUTE = '/profile';
const SUPER_ADMIN_DEFAULT_ROUTE = '/dashboard';

const getUserModules = (user) => {
  if (!user) {
    return [];
  }
  const modules = Array.isArray(user.modules) ? user.modules : [];
  const normalized = modules
    .filter(Boolean)
    .map((moduleId) => normalizeModuleId(String(moduleId)));
  return Array.from(new Set(normalized));
};

const getDefaultModuleRoute = (user) => {
  if (!user) {
    return '/login';
  }
  if (user.role === 'super_admin') {
    return SUPER_ADMIN_DEFAULT_ROUTE;
  }
  const modules = getUserModules(user);
  const match = MODULE_ROUTE_ORDER.find(({ moduleIds }) =>
    moduleIds.some((id) => modules.includes(id))
  );
  return match?.path || NO_MODULE_FALLBACK_ROUTE;
};

const hasModuleAccess = (user, moduleId) => {
  if (!moduleId) {
    return true;
  }
  if (!user) {
    return false;
  }
  if (user.role === 'super_admin') {
    return true;
  }
  return getUserModules(user).includes(moduleId);
};

const sortZipOptions = (options) =>
  [...options].sort((a, b) => {
    const nameCompare = (a.gemeente || '').localeCompare(b.gemeente || '');
    if (nameCompare !== 0) {
      return nameCompare;
    }
    return (a.zipcode || '').localeCompare(b.zipcode || '');
  });

const normalizeSearchValue = (value) =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const buildSearchValue = (zipcode, gemeente) =>
  normalizeSearchValue(`${zipcode || ''} ${gemeente || ''}`);

const normalizeCityOptions = (cities) => {
  const map = new Map();
  (Array.isArray(cities) ? cities : []).forEach((city) => {
    const zipcode = String(city?.zipcode ?? '').trim();
    const name = (city?.name ?? city?.municipality ?? '').trim();
    if (!zipcode || !name) {
      return;
    }
    const searchValue = buildSearchValue(zipcode, name);
    const key = `${zipcode}|${name}`;
    if (!map.has(key)) {
      map.set(key, {
        zipcode,
        gemeente: name,
        searchValue,
      });
    }
  });
  return sortZipOptions(Array.from(map.values()));
};

const fetchCityOptionsFromApi = async () => {
  try {
    const response = await axios.get(`${API}/location/cities/by-access?include_all_if_none=true`);
    return normalizeCityOptions(response.data);
  } catch (error) {
    console.warn('Failed to load city options by access, falling back to full list:', error?.response?.status || error);
    try {
      const fallbackResponse = await axios.get(`${API}/location/cities`);
      return normalizeCityOptions(fallbackResponse.data);
    } catch (fallbackError) {
      console.error('Failed to load fallback city options:', fallbackError);
      return [];
    }
  }
};

const filterCityOptionsByAccess = (options, allowedZipcodes) => {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }
  if (!Array.isArray(allowedZipcodes) || allowedZipcodes.length === 0) {
    return options;
  }
  const allowedSet = new Set(allowedZipcodes.map((zip) => String(zip)));
  return options.filter(({ zipcode }) => allowedSet.has(zipcode));
};

// Debug logging
console.log('Backend URL:', BACKEND_URL);
console.log('API URL:', API);

// Token utilities
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error parsing token:', error);
    return true;
  }
};

const getTokenExpirationTime = (token) => {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000; // Convert to milliseconds
  } catch (error) {
    console.error('Error parsing token expiration:', error);
    return null;
  }
};

// Auth Context
export const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Setup axios interceptors
  useEffect(() => {
    // Response interceptor to handle token expiration
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          // If we're already refreshing, don't start another refresh
          if (isRefreshing) {
            return Promise.reject(error);
          }
          
          // Try to refresh the token
          const refreshResult = await refreshToken();
          if (refreshResult.success) {
            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${refreshResult.token}`;
            return axios(originalRequest);
          } else {
            // Refresh failed, logout user
            logout();
            return Promise.reject(error);
          }
        }

        const forbiddenDetail = error.response?.data?.detail;
        const isAuthForbidden = error.response?.status === 403 && (
          forbiddenDetail === 'Not authenticated'
          || (typeof forbiddenDetail === 'string' && forbiddenDetail.toLowerCase().includes('not authenticated'))
        );
        if (isAuthForbidden && !originalRequest._retry) {
          originalRequest._retry = true;
          if (!isRefreshing) {
            const refreshResult = await refreshToken();
            if (refreshResult.success) {
              originalRequest.headers.Authorization = `Bearer ${refreshResult.token}`;
              return axios(originalRequest);
            }
            logout();
          }
        }
        
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [isRefreshing]);

  useEffect(() => {
    if (token) {
      // Check if token is expired before using it
      if (isTokenExpired(token)) {
        console.log('Token is expired, attempting refresh...');
        refreshToken().then((result) => {
          if (result.success) {
            fetchCurrentUser();
          } else {
            logout();
          }
        });
      } else {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchCurrentUser();
      }
    } else {
      setLoading(false);
    }
  }, [token]);

  // Set up automatic token refresh before expiration
  useEffect(() => {
    if (!token) return;

    const expirationTime = getTokenExpirationTime(token);
    if (!expirationTime) return;

    // Refresh token 5 minutes before expiration
    const refreshTime = expirationTime - (5 * 60 * 1000);
    const currentTime = Date.now();
    
    if (refreshTime > currentTime) {
      const timeoutId = setTimeout(() => {
        console.log('Proactively refreshing token...');
        refreshToken();
      }, refreshTime - currentTime);

      return () => clearTimeout(timeoutId);
    } else {
      // Token expires soon, refresh immediately
      refreshToken();
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only logout if it's not a token expiration (401) - let interceptor handle that
      if (error.response?.status !== 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async () => {
    if (isRefreshing) {
      return { success: false, error: 'Already refreshing' };
    }

    setIsRefreshing(true);
    try {
      const currentToken = localStorage.getItem('token');
      if (!currentToken) {
        return { success: false, error: 'No token to refresh' };
      }

      // Call refresh endpoint with current (expired) token
      // Backend will validate signature and create new token
      const response = await axios.post(`${API}/auth/refresh`, { token: currentToken });
      const { access_token, user: userData } = response.data;
      
      // Update token in localStorage and state
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true, token: access_token, user: userData };
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, logout user
      logout();
      return { success: false, error: error.response?.data?.detail || 'Token refresh failed' };
    } finally {
      setIsRefreshing(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      // Store only the token - never store credentials for security
      localStorage.setItem('token', access_token);
      
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true, user: userData };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  // Registration function removed - registration is disabled

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const impersonateUser = async (userId) => {
    try {
      const response = await axios.post(`${API}/admin/impersonate/${userId}`);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true, user: userData };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Impersonation failed' 
      };
    }
  };

  const value = {
    user,
    token,
    login,
    logout,
    impersonateUser,
    refreshToken,
    isRefreshing,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Components
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Only login is allowed - registration is disabled
    const result = await login(email, password);

    if (result.success) {
      navigate(getDefaultModuleRoute(result.user));
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage('');
    setForgotSuccess(false);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotEmail
      });

      setForgotSuccess(response.data.success);
      setForgotMessage(response.data.message);
      
      if (response.data.success) {
        // Clear form after success
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotEmail('');
          setForgotMessage('');
        }, 3000);
      }
    } catch (error) {
      setForgotSuccess(false);
      setForgotMessage(error.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
        <CardTitle className="text-2xl font-bold text-emerald-900 flex justify-center">
            <img
              src={logoImage}
              alt="ProjectLeads"
              className="h-10 w-auto"
              style={{ maxWidth: '200px', height: '60px', objectFit: 'contain' }}
            />
          </CardTitle>
          <CardDescription>
            Inloggen op uw account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Wachtwoord</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && (
              <div className="text-red-600 text-sm text-center">{error}</div>
            )}
            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700" 
              disabled={loading}
            >
              {loading ? 'Bezig...' : 'Inloggen'}
            </Button>
          </form>
          
          {/* Forgot Password Link */}
          <div className="mt-4 text-center">
            <Button
              variant="link"
              onClick={() => {
                setShowForgotPassword(true);
                setForgotMessage('');
                setForgotSuccess(false);
              }}
              className="text-emerald-600 text-sm"
            >
              Wachtwoord vergeten?
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wachtwoord resetten</DialogTitle>
            <DialogDescription>
              Voer uw e-mailadres in. We sturen u een nieuw wachtwoord toe.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email">E-mailadres</Label>
              <Input
                id="forgot-email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="uw-email@example.com"
                required
                disabled={forgotLoading}
              />
            </div>

            {forgotMessage && (
              <div className={`text-sm p-3 rounded-lg ${
                forgotSuccess 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {forgotMessage}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotEmail('');
                  setForgotMessage('');
                }}
                className="flex-1"
                disabled={forgotLoading}
              >
                Annuleren
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={forgotLoading}
              >
                {forgotLoading ? 'Bezig...' : 'Reset wachtwoord'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    new: { label: 'Nieuw', variant: 'default', icon: Clock },
    interessant: { label: 'Favorieten', variant: '  ', icon: Star },
    gelezen: { label: 'Gelezen', variant: 'gelezen', icon: Eye },
    gecontacteerd: { label: 'Gecontacteerd', variant: 'default', icon: CheckCircle },
    /*lopend: { label: 'Lopend', variant: 'outline', icon: Clock },
    gearchiveerd: { label: 'Gearchiveerd', variant: 'secondary', icon: Archive }*/
  };

  const config = statusConfig[status] || statusConfig.new;
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className="flex items-center gap-1">
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

// Reusable Header component
export const AppHeader = ({ user, currentPage, onNavigate, onLogout }) => {
  const canSeePermits = hasModuleAccess(user, 'permits');
  const canSeeOOWoningen = hasModuleAccess(user, 'oo_woningen');
  const canSeeVacantSpaces = hasModuleAccess(user, 'vacant_spaces');
  
  return (
    <header className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 min-w-0">
          <div className="flex items-center space-x-4 md:space-x-6 flex-shrink-0 min-w-0">
            <img src={logoImage} alt="ProjectLeads" className="h-10 w-auto flex-shrink-0" style={{ maxWidth: '200px', height: '60px', objectFit: 'contain' }} /> 
            <nav className="hidden lg:flex items-center space-x-3 md:space-x-4 flex-wrap min-w-0">
              {canSeePermits && (
                <Button
                  variant="ghost"
                  onClick={() => onNavigate('/dashboard')}
                  className={`whitespace-nowrap ${currentPage === 'dashboard' ? 'menu-title font-medium' : 'text-gray-600 hover:menu-title'}`}
                >
                  Omgevingsvergunningen
                </Button>
              )}


              {(user?.role === 'super_admin' || canSeeOOWoningen) && (
                <Button
                  variant="ghost"
                  onClick={() => onNavigate('/oo-woningen')}
                  className={`whitespace-nowrap ${currentPage === 'oo-woningen' ? 'menu-title font-medium' : 'text-gray-600 hover:menu-title'}`}
                >
                  Ongeschikt Onbewoonbaar
                </Button>
              )}
              {(user?.role === 'super_admin' || canSeeVacantSpaces) && (
                <Button
                  variant="ghost"
                  onClick={() => onNavigate('/vacant-spaces')}
                  className={`whitespace-nowrap ${currentPage === 'vacant-spaces' ? 'menu-title font-medium' : 'text-gray-600 hover:menu-title'}`}
                >
                  Leegstand
                </Button>
              )}
              {canSeePermits && (
                <Button
                  variant="ghost"
                  onClick={() => onNavigate('/prospectie')}
                  className={`whitespace-nowrap flex items-center gap-2 ${currentPage === 'prospectie' ? 'menu-title font-medium' : 'text-gray-600 hover:menu-title'}`}
                >
                  <Mail className="w-4 h-4" />
                  Campagnes
                </Button>
              )}
              {user?.role === 'admin' || user?.role === 'super_admin' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`whitespace-nowrap ${currentPage === 'admin' || currentPage === 'mijnperceel' || currentPage === 'admin-modules' ? 'menu-title font-medium' : 'text-gray-600 hover:menu-title'}`}
                    >
                      Admin
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {user?.role === 'super_admin' && (
                      <DropdownMenuItem onClick={() => onNavigate('/mijnperceel')}>
                        <Settings className="w-4 h-4 mr-2" />
                        Sync settings
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => onNavigate('/admin')}>
                      <Users className="w-4 h-4 mr-2" />
                      User management
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </nav>
          </div>
          <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <Badge variant="secondary" className="whitespace-nowrap hidden sm:inline-flex">
                {user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
              </Badge>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 whitespace-nowrap">
                  <UserCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm hidden sm:inline truncate max-w-[170px]">{user?.full_name}</span>
                  <ChevronDown className="w-4 h-4 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => onNavigate('/profile')}>
                  <User className="w-4 h-4 mr-2" />
                  Profiel
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Uitloggen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

// Coordinate conversion helper (Lambert 72 EPSG:31370 to WGS84 EPSG:4326)
const convertLambert72ToWGS84 = (x, y) => {
  try {
    // Convert from Lambert 72 (EPSG:31370) to WGS84 (EPSG:4326)
    // Note: x is longitude-like, y is latitude-like in Lambert 72
    const [lon, lat] = proj4("EPSG:31370", "EPSG:4326", [x, y]);
    return { lat, lon };
  } catch (error) {
    console.error('Coordinate conversion error:', error);
    return null;
  }
};

const getOOWoningLocation = (listing) => {
  if (!listing) return null;

  const extractPoint = (geometry) => {
    if (!geometry) return null;
    if (geometry.type === 'Feature' && geometry.geometry) {
      return geometry.geometry;
    }
    return geometry;
  };

  const pointGeom = extractPoint(listing.geometry);
  if (pointGeom && pointGeom.type === 'Point' && Array.isArray(pointGeom.coordinates)) {
    const [lon, lat] = pointGeom.coordinates;
    if (
      typeof lat === 'number' &&
      typeof lon === 'number'
    ) {
      if (Math.abs(lat) > 1000 || Math.abs(lon) > 1000) {
        const converted = convertLambert72ToWGS84(lon, lat);
        if (converted && converted.lat && converted.lon) {
          return converted;
        }
      }
      return { lat, lon };
    }
  }

  if (
    listing.lat != null &&
    listing.lon != null &&
    Number.isFinite(Number(listing.lat)) &&
    Number.isFinite(Number(listing.lon))
  ) {
    return { lat: Number(listing.lat), lon: Number(listing.lon) };
  }

  return null;
};

// Map component for lead location using Google Maps
const LeadLocationMap = ({ location }) => {
  // Google Maps loader configuration - must be inside component
  const { isLoaded: isGoogleMapsLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['marker'], // Enable AdvancedMarkerElement support
  });

  const [coords, setCoords] = useState(null);
  const [mapType, setMapType] = useState('satellite'); // 'satellite', 'street', or 'hybrid'
  const [mapInstance, setMapInstance] = useState(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (location && (location.lat !== null && location.lat !== undefined) && (location.lon !== null && location.lon !== undefined)) {
      // Check if coordinates are in Lambert 72 range (Belgian projection EPSG:31370)
      // Lambert 72 coordinates are typically: X: 100000-300000, Y: 150000-550000
      // WGS84 coordinates are: lat: -90 to 90, lon: -180 to 180
      if (location.lat > 1000 || location.lon > 1000 || location.lat < -1000 || location.lon < -1000) {
        // Likely Lambert 72 coordinates (EPSG:31370) - convert to WGS84 (EPSG:4326)
        // In Lambert 72: x is easting, y is northing
        const converted = convertLambert72ToWGS84(location.lon, location.lat);
        if (converted && converted.lat && converted.lon) {
          setCoords(converted);
        } else {
          console.warn('Failed to convert coordinates:', location);
          setCoords(null);
        }
      } else {
        // Already in WGS84 lat/lon (EPSG:4326)
        setCoords({ lat: location.lat, lon: location.lon });
      }
    } else {
      setCoords(null);
    }
  }, [location]);

  // Create AdvancedMarkerElement when map is loaded and coordinates are available
  useEffect(() => {
    // Clean up previous marker
    if (markerRef.current) {
      markerRef.current.map = null;
      markerRef.current = null;
    }

    if (isGoogleMapsLoaded && mapInstance && coords && window.google && window.google.maps) {
      // Try AdvancedMarkerElement first (requires Map ID)
      if (GOOGLE_MAPS_MAP_ID && window.google.maps.marker) {
        try {
          const { AdvancedMarkerElement } = window.google.maps.marker;
          const marker = new AdvancedMarkerElement({
            map: mapInstance,
            position: { lat: coords.lat, lng: coords.lon },
            title: location?.address || 'Locatie',
          });
          markerRef.current = marker;
        } catch (error) {
          console.warn('AdvancedMarkerElement failed, falling back to Marker:', error);
          // Fallback to regular Marker
          if (window.google.maps.Marker) {
            const marker = new window.google.maps.Marker({
              map: mapInstance,
              position: { lat: coords.lat, lng: coords.lon },
              title: location?.address || 'Locatie',
            });
            markerRef.current = marker;
          }
        }
      } else {
        // Use regular Marker if no Map ID is provided
        if (window.google.maps.Marker) {
          const marker = new window.google.maps.Marker({
            map: mapInstance,
            position: { lat: coords.lat, lng: coords.lon },
            title: location?.address || 'Locatie',
          });
          markerRef.current = marker;
        }
      }
    }

    // Cleanup function
    return () => {
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
    };
  }, [isGoogleMapsLoaded, mapInstance, coords, location?.address]);

  if (!isGoogleMapsLoaded) {
    return (
      <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center text-sm text-gray-500">
        Kaart wordt geladen...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="h-64 bg-gray-100 rounded-lg flex flex-col items-center justify-center p-4 text-sm text-red-500">
        <p className="font-semibold mb-2">Fout bij het laden van de kaart</p>
        <p className="text-xs text-gray-600 text-center">
          {loadError.message || 'Controleer uw Google Maps API configuratie'}
        </p>
        {!GOOGLE_MAPS_API_KEY && (
          <p className="text-xs text-red-400 mt-2">
            Waarschuwing: Geen API key gevonden
          </p>
        )}
      </div>
    );
  }

  if (!coords || !coords.lat || !coords.lon) {
    return (
      <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center text-sm text-gray-500">
        Geen kaartlocatie beschikbaar
      </div>
    );
  }

  // Google Maps map type constants
  const getMapTypeId = () => {
    if (mapType === 'satellite') return 'satellite';
    if (mapType === 'hybrid') return 'hybrid';
    return 'roadmap'; // street
  };

  const mapContainerStyle = {
    height: '100%',
    width: '100%'
  };

  const mapCenter = {
    lat: coords.lat,
    lng: coords.lon
  };

  const handleMapLoad = (map) => {
    setMapInstance(map);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">Locatie</h3>
        <div className="flex gap-2">
          <Button
            variant={mapType === 'street' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMapType('street')}
            className="h-7 text-xs"
          >
            Straat
          </Button>
          <Button
            variant={mapType === 'hybrid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMapType('hybrid')}
            className="h-7 text-xs"
          >
            Hybride
          </Button>
          <Button
            variant={mapType === 'satellite' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setMapType('satellite')}
            className="h-7 text-xs"
          >
            Satelliet
          </Button>
        </div>
      </div>
      <div className="h-64 rounded-lg overflow-hidden border border-gray-200">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={17}
          options={{
            mapId: GOOGLE_MAPS_MAP_ID || undefined, // Map ID required for Advanced Markers
            mapTypeId: getMapTypeId(),
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
          onLoad={handleMapLoad}
          onUnmount={() => {
            if (markerRef.current) {
              markerRef.current.map = null;
              markerRef.current = null;
            }
            setMapInstance(null);
          }}
        />
      </div>
      <p className="text-xs text-gray-500">
        {location?.address || 'Locatie'}
      </p>
    </div>
  );
};

// Lead detail sheet component
const DATE_RANGE_OPTIONS = [
  { value: '15', label: 'Laatste 15 dagen' },
  { value: '30', label: 'Laatste 30 dagen' },
  { value: '90', label: 'Laatste 90 dagen' },
  { value: 'all', label: 'Alle datums' },
];

const buildDaysBackParams = (selectedDays, params = {}) => {
  if (selectedDays && selectedDays !== 'all') {
    params.days_back = selectedDays;
  } else {
    params.freshness = 'all';
  }
  return params;
};

const fetchLeadCategoryGroups = async () => {
  try {
    const response = await axios.get(`${API}/leads/categories/groups`, {
      headers: getAuthHeaders(),
    });
    return response.data?.groups || [];
  } catch (error) {
    console.error('Failed to load category groups:', error);
    return [];
  }
};

const CategoryMultiSelect = ({ groups, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open) {
      setCollapsedGroups(new Set(groups.map((group) => group.id ?? group.name)));
    }
  }, [open, groups]);

  const selectedSet = new Set(selected);
  const totalCategories = groups.reduce((acc, g) => acc + g.categories.length, 0);

  const toggleCategory = (name) => {
    const next = new Set(selectedSet);
    if (next.has(name)) {
      next.delete(name);
    } else {
      next.add(name);
    }
    onChange([...next]);
  };

  const toggleGroup = (group) => {
    const allSelected = group.categories.every((c) => selectedSet.has(c));
    const next = new Set(selectedSet);
    if (allSelected) {
      group.categories.forEach((c) => next.delete(c));
    } else {
      group.categories.forEach((c) => next.add(c));
    }
    onChange([...next]);
  };

  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const buttonLabel = selected.length === 0
    ? 'Alle categorieën'
    : `${selected.length} van ${totalCategories} geselecteerd`;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-12 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-base"
      >
        <span className={selected.length === 0 ? 'text-gray-500' : 'text-gray-900'}>
          {buttonLabel}
        </span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-md border border-gray-200 bg-white p-2 shadow-lg">
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mb-1 w-full rounded px-2 py-1 text-left text-xs text-blue-600 hover:bg-blue-50"
            >
              Selectie wissen
            </button>
          )}
          {groups.length === 0 && (
            <p className="px-2 py-1 text-xs text-gray-500">Geen categorieën beschikbaar</p>
          )}
          {groups.map((group) => {
            const groupKey = group.id ?? group.name;
            const isCollapsed = collapsedGroups.has(groupKey);
            const allSelected = group.categories.length > 0 && group.categories.every((c) => selectedSet.has(c));
            const someSelected = group.categories.some((c) => selectedSet.has(c));
            const selectedInGroup = group.categories.filter((c) => selectedSet.has(c)).length;
            return (
              <div key={groupKey} className="mb-2">
                <div className="flex items-center gap-1 rounded px-1 py-1 hover:bg-gray-50">
                  <button
                    type="button"
                    onClick={() => toggleGroupCollapse(groupKey)}
                    className="p-1 text-gray-500"
                    aria-label={isCollapsed ? 'Categorieën uitklappen' : 'Categorieën inklappen'}
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${isCollapsed ? '-rotate-90' : 'rotate-0'}`} />
                  </button>
                  <label className="flex flex-1 items-center gap-2 cursor-pointer font-medium">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={() => toggleGroup(group)}
                    />
                    <span className="text-sm">{group.name}</span>
                    {isCollapsed && selectedInGroup > 0 && (
                      <span className="text-xs text-gray-500">({selectedInGroup})</span>
                    )}
                  </label>
                </div>
                {!isCollapsed && (
                  <div className="ml-8">
                    {group.categories.map((name) => (
                      <label key={name} className="flex items-center gap-2 rounded px-2 py-1 cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={selectedSet.has(name)}
                          onChange={() => toggleCategory(name)}
                        />
                        <span className="text-sm text-gray-700">
                          {name.length > 55 ? `${name.substring(0, 55)}...` : name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const LeadDetailSheet = ({ lead, onClose, onUpdate, fetchLeads, fetchSummary }) => {
  const [viewLead, setViewLead] = useState(lead);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!lead?.id) {
      setViewLead(null);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    setViewLead(lead);

    axios
      .get(`${API}/leads/${lead.id}`, { headers: getAuthHeaders() })
      .then((response) => {
        if (!cancelled) {
          setViewLead(response.data);
        }
      })
      .catch((error) => {
        console.error('Failed to load lead details:', error);
        if (!cancelled) {
          setViewLead(lead);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDetailLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [lead?.id]);

  // Extract status value - handle both enum objects and plain strings
  const initialStatus = typeof viewLead?.status === 'object' ? viewLead.status.value : (viewLead?.status || 'new');
  const [status, setStatus] = useState(initialStatus);
  const [isFavorite, setIsFavorite] = useState(viewLead?.is_favorite || false);
  const [isInteresant, setIsInteresant] = useState(viewLead?.status=='interessant' || false);
  const [updating, setUpdating] = useState(false);

  // Update status and favorite when lead changes
  useEffect(() => {
    if (viewLead) {
      const newStatus = typeof viewLead.status === 'object' ? viewLead.status.value : (viewLead.status || 'new');
      setStatus(newStatus);
      setIsFavorite(viewLead.is_favorite || false);
      setIsInteresant(viewLead.status=='interessant' || false);
    }
  }, [viewLead]);

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        status,
        is_favorite: isFavorite
      };
      
      console.log('Updating lead:', viewLead.id);
      console.log('Update payload:', updateData);
      
      const response = await axios.patch(`${API}/leads/${viewLead.id}`, updateData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Update successful:', response.data);
      onUpdate();
    } catch (error) {
      console.error('Failed to update lead:', error);
      console.error('Error response:', error.response?.data);
      alert(`Failed to update lead: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleFavorite = async (leadId, isFavorite) => {
    try {
      // Update lead to favorite and set status to interessant if becoming favorite
      const updateData = {
        is_favorite: isFavorite,
        //...(isFavorite && { status: 'interessant' })
      };

      await axios.patch(`${API}/leads/${leadId}`, updateData);
      
      // Update local state
      /*setLeads(prevLeads => 
        prevLeads.map(lead => 
          lead.id === leadId 
            ? { 
                ...lead, 
                is_favorite: isFavorite,
                //...(isFavorite && { status: 'interessant' })
              }
            : lead
        )
      );*/

          // Refresh the Dashboard by calling the passed functions
    if (fetchLeads) {
      fetchLeads();
    }
    if (fetchSummary) {
      fetchSummary();
    }     

    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const handleToggleStatus = async (leadId, isInteresant) => {
    try {
      // Update lead to favorite and set status to interessant if becoming favorite
      const updateData = {
        //is_favorite: isFavorite,
        ...(!isInteresant ? { status: 'gelezen' } : { status: 'interessant' })
      };

      await axios.patch(`${API}/leads/${leadId}`, updateData);
      
      // Update local state
     /* setLeads(prevLeads => 
        prevLeads.map(lead => 
          lead.id === leadId 
            ? { 
                ...lead, 
                //is_favorite: isFavorite,
                ...(!isInteresant ? { status: 'gelezen' } : { status: 'interessant' })
              }
            : lead
        )
      );*/
          // Refresh the Dashboard by calling the passed functions
        if (fetchLeads) {
          fetchLeads();
        }
        if (fetchSummary) {
          fetchSummary();
        }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  // Helper function to get WGS84 coordinates for Street View
  const getStreetViewCoordinates = () => {
    if (!viewLead?.location || (!viewLead.location.lat && !viewLead.location.lon)) {
      return null;
    }

    const { lat, lon } = viewLead.location;
    
    // Check if coordinates are in Lambert 72 range (Belgian projection EPSG:31370)
    // Lambert 72 coordinates are typically: X: 100000-300000, Y: 150000-550000
    // WGS84 coordinates are: lat: -90 to 90, lon: -180 to 180
    if (lat > 1000 || lon > 1000 || lat < -1000 || lon < -1000) {
      // Likely Lambert 72 coordinates (EPSG:31370) - convert to WGS84 (EPSG:4326)
      const converted = convertLambert72ToWGS84(lon, lat);
      if (converted && converted.lat && converted.lon) {
        return { lat: converted.lat, lon: converted.lon };
      }
      return null;
    } else {
      // Already in WGS84 lat/lon (EPSG:4326)
      return { lat, lon };
    }
  };

  const streetViewUrl = useMemo(() => {
    if (!viewLead?.location || (!viewLead.location.lat && !viewLead.location.lon)) {
      return '';
    }
    const coords = getStreetViewCoordinates();
    if (!coords || !coords.lat || !coords.lon) {
      return '';
    }
    return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${coords.lat},${coords.lon}&layer=c`;
  }, [viewLead]);

  if (!lead || !viewLead) return null;

  return (
    <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
      <SheetHeader>
        <SheetTitle>
          {viewLead.title}
        </SheetTitle>
        <SheetDescription>
          Ref: {viewLead.ref_number}
        </SheetDescription>
      </SheetHeader>

      {detailLoading && (
        <p className="mt-4 text-sm text-gray-500">Details laden...</p>
      )}

      <div className="mt-6 space-y-6">
        {/* Status and Actions */}
        <div className="space-y-4">
        { /*  <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">Nieuw</SelectItem>
                <SelectItem value="interessant">Interessant</SelectItem>
                <SelectItem value="verwijderd">Verwijderd</SelectItem>
                <SelectItem value="lopend">Lopend</SelectItem>
                <SelectItem value="gecontacteerd">Gecontacteerd</SelectItem>
                <SelectItem value="offerte">Offerte</SelectItem>
                <SelectItem value="gearchiveerd">Gearchiveerd</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
         <Button 
            onClick={handleUpdate} 
            disabled={updating}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            {updating ? 'Opslaan...' : 'Wijzigingen opslaan'}
          </Button>
          */}
          <Button
            variant="ghost"
            size="sm"
            className={` p-1 h-8 w-8 rounded-full hover:bg-gray-100 ${
              viewLead.status=='interessant' ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
            }`}
            onClick={(e) => {
                e.stopPropagation();
                handleToggleStatus(viewLead.id, !isInteresant);
                setIsInteresant(!isInteresant);
            }}
            >
            <Star className={`w-5 h-5 ${isInteresant ? 'text-yellow-500 bg-red-50 rounded p-0.5' : 'text-gray-400'}`} style={{ width: '30px', height: '30px',marginRight: '10px' }} />                              
          </Button>
          <Button
              variant="ghost"
              size="sm"
              /*onClick={() => setIsFavorite(!isFavorite)}*/
              onClick={() => {handleToggleFavorite(viewLead.id, !isFavorite);setIsFavorite(!isFavorite)}}
              className={isFavorite ? "text-red-500" : "text-gray-400"}
            >
              <Mail className={`w-4 h-4 ${isFavorite ? 'text-red-500 bg-red-50 rounded p-0.5' : 'text-gray-400'}`} style={{ width: '30px', height: '30px' }} />
            </Button>
          
        </div>

        <Separator />

        {/* Basic Info */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg">Basisgegevens</h3>

          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <MapPin className="w-4 h-4" />
            <span>{viewLead?.street} {viewLead?.number}, {viewLead.zipcode} {viewLead.municipality}</span>
          </div>


          
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Building className="w-4 h-4" />
            <span>{viewLead.permit_type}</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>{new Date(viewLead.datum_aanvraag).toLocaleDateString('nl-NL')}</span>
          </div>
          
          {viewLead.ref_number && (
            <div className="pt-2">
              <a 
                href={`https://omgevingsloketinzage.omgeving.vlaanderen.be/${viewLead.ref_number}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Bekijk in Omgevingsloket</span>
              </a>
            </div>
          )}
        </div>
        

        <Separator />

        {/* Map Preview */}
        {viewLead.location && (viewLead.location.lat || viewLead.location.lon) && (
          <>
            <LeadLocationMap location={viewLead.location} />
            {streetViewUrl && (
              <div className="pt-2">
                <Button
                  asChild
                  variant="outline"
                  className="w-full justify-center gap-2"
                >
                  <a
                    href={streetViewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Bekijk in Google Street View</span>
                  </a>
                </Button>
              </div>
            )}
            <Separator />
          </>
        )}


        {/* Description */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg">Beschrijving</h3>
          <p className="text-sm text-gray-700 leading-relaxed">
            {viewLead.description}
          </p>
        </div>

        <Separator />

        {/* Contact Information */}
        {viewLead.eigenaar_info && (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Eigenaar</h3>
            <div className="space-y-2">
              {viewLead.eigenaar_info.name && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4" />
                  <span>{viewLead.eigenaar_info.name}</span>
                </div>
              )}
              {viewLead.eigenaar_info.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4" />
                  <span>{viewLead.eigenaar_info.email}</span>
                </div>
              )}
              {viewLead.eigenaar_info.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4" />
                  <span>{viewLead.eigenaar_info.phone}</span>
                </div>
              )}
              {viewLead.eigenaar_info.address && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4" />
                  <span>{viewLead.eigenaar_info.address}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {viewLead.architect_info && (
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Architect</h3>
            <div className="space-y-2">
              {viewLead.architect_info.name && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4" />
                  <span>{viewLead.architect_info.name}</span>
                </div>
              )}
              {viewLead.architect_info.company && (
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="w-4 h-4" />
                  <span>{viewLead.architect_info.company}</span>
                </div>
              )}
              {viewLead.architect_info.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4" />
                  <span>{viewLead.architect_info.email}</span>
                </div>
              )}
              {viewLead.architect_info.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4" />
                  <span>{viewLead.architect_info.phone}</span>
                </div>
              )}
              {viewLead.architect_info.address && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4" />
                  <span>{viewLead.architect_info.address}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <Separator />

        {/* Authority */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg">Bevoegde overheid</h3>
          <p className="text-sm text-gray-700">{viewLead.bevoegde_overheid}</p>
        </div>
      </div>
    </SheetContent>
  );
};

const OOWoningDetailSheet = ({ listing, onToggleFavorite, onToggleStatus }) => {
  if (!listing) return null;

  const fullAddress = `${listing.straat || ''} ${listing.huisnummer || ''}${
    listing.busnummer ? ` bus ${listing.busnummer}` : ''
  }`.trim();

  const formatAreaValue = (value) =>
    typeof value === 'number' && Number.isFinite(value) && value > 0
      ? `${Math.round(value).toLocaleString('nl-BE')} m²`
      : 'Onbekend';

  const detailItems = [
    { label: 'Categorie', value: listing.categorie },
    { label: 'Thema', value: listing.thema },
    { label: 'Auteur', value: listing.auteur },
    { label: 'Gemeente', value: listing.gemeente },
    { label: 'Provincie', value: listing.provincie },
  ].filter((item) => item.value);
  const ooLocation = getOOWoningLocation(listing);

  const streetViewLocation = useMemo(() => {
    if (!listing) return null;
    const geom =
    listing?.geometry?.type === 'Feature'
        ? listing.geometry.geometry
        : listing.geometry;
    if (geom?.type === 'Point' && Array.isArray(geom.coordinates)) {
      const [lon, lat] = geom.coordinates;
      if (typeof lat === 'number' && typeof lon === 'number' && Number.isFinite(lat) && Number.isFinite(lon)) {
        return { lat, lon };
      }
    }
    return null;
  }, [listing]);

  return (
    <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
      <SheetHeader>
        <SheetTitle className="flex flex-wrap items-center gap-2">
          <span>{listing.naam || `OO-woning ${listing.poiid}`}</span>
          {/*<StatusBadge status={listing.status} />*/}
        </SheetTitle>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 rounded-full hover:bg-gray-100 ${
              listing.status === 'interessant'
                ? 'text-yellow-500 bg-red-50'
                : 'text-gray-400 hover:text-red-500'
            }`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleStatus?.(listing);
            }}
          >
            <Star className="w-4 h-4" style={{ width: '30px', height: '30px' }} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 rounded-full hover:bg-gray-100 ${
              listing.is_favorite ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:text-red-500'
            }`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleFavorite?.(listing, !listing.is_favorite);
            }}
          >
            <Mail className="w-4 h-4" style={{ width: '30px', height: '30px' }} />
          </Button>
        </div>
        <SheetDescription className="flex items-center gap-3 flex-wrap">
          <span>POIID: {listing.poiid}</span>
          {listing.last_seen_at && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Laatst gezien: {new Date(listing.last_seen_at).toLocaleDateString('nl-NL')}
            </span>
          )}
        </SheetDescription>
      </SheetHeader>

      <div className="mt-6 space-y-6">
        <div className="space-y-3">
          <h3 className="font-semibold text-lg">Locatie</h3>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <MapPin className="w-4 h-4" />
            <span>{fullAddress || 'Onbekend adres'}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Building className="w-4 h-4" />
            <span>{listing.postcode || '-'} {listing.gemeente || ''}</span>
          </div>
          {listing.provincie && (
            <Badge variant="outline" className="mt-2 w-fit">
              {listing.provincie}
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-gray-500">Perceeloppervlakte</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">
              {formatAreaValue(listing.parcel_area_m2)}
            </p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-gray-500">Gebouwoppervlakte</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">
              {formatAreaValue(listing.building_area_m2)}
            </p>
          </div>
        </div>

        {detailItems.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <h3 className="font-semibold text-lg">Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {detailItems.map((item) => (
                  <div key={item.label} className="p-3 border border-gray-100 rounded-lg">
                    <p className="text-xs text-gray-500">{item.label}</p>
                    <p className="mt-1 font-medium text-gray-900">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {listing.omschrijving && (
          <>
            <Separator />
            <div className="space-y-3">
              <h3 className="font-semibold text-lg">Beschrijving</h3>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                {listing.omschrijving}
              </p>
            </div>
          </>
        )}

        {ooLocation && (
          <>
            <Separator />
            <div className="space-y-3">           
                <LeadLocationMap
                  location={{
                    lat: ooLocation.lat,
                    lon: ooLocation.lon,
                    address: fullAddress,
                  }}
                />              
                <div className="space-y-3">
                      <Button
                        asChild
                        variant="outline"
                        className="w-full justify-center gap-2"
                      >
                        <a
                          href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${ooLocation.lat},${ooLocation.lon}&layer=c`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="w-4 h-4" />
                          <span>Bekijk in Google Street View</span>
                        </a>
                      </Button>
                </div>                
              </div>            
          </>
        )}

        {(listing.metadata_url || listing.link1 || listing.link2) && (
          <>
            <Separator />
            <div className="space-y-3">
              <h3 className="font-semibold text-lg">Links</h3>
              <div className="flex flex-col gap-2">
                {listing.metadata_url && (
                  <Button
                    variant="outline"
                    className="justify-start"
                    onClick={() => window.open(listing.metadata_url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Metadata
                  </Button>
                )}
                {listing.link1 && (
                  <Button
                    variant="outline"
                    className="justify-start"
                    onClick={() => window.open(listing.link1, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Meer info
                  </Button>
                )}
                {listing.link2 && (
                  <Button
                    variant="outline"
                    className="justify-start"
                    onClick={() => window.open(listing.link2, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Extra link
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </SheetContent>
  );
};

// Prospection Module Component
const ProspectieModule = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [ooFavorites, setOoFavorites] = useState([]);
  const [ooLoading, setOoLoading] = useState(false);
  const [selectedOoListings, setSelectedOoListings] = useState(new Set());
  const [ooCardCollapsed, setOoCardCollapsed] = useState(false);
  const [selectedOOWoning, setSelectedOOWoning] = useState(null);
  const [ooSheetOpen, setOoSheetOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadsFetchError, setLeadsFetchError] = useState('');
  const [activeTab, setActiveTab] = useState('post');
  const [credits, setCredits] = useState(0);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const CAMPAIGN_PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(CAMPAIGN_PAGE_SIZE);
  const [hasMoreLeads, setHasMoreLeads] = useState(false);
  const [totalMatchingLeads, setTotalMatchingLeads] = useState(0);
  const [campaignFilteredCount, setCampaignFilteredCount] = useState(0);
  const [selectingAll, setSelectingAll] = useState(false);
  
  // Lead detail drawer
  const [selectedLead, setSelectedLead] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedZipcodes, setSelectedZipcodes] = useState([]);  // Multi-select zipcodes
  const [zipDropdownOpen, setZipDropdownOpen] = useState(false);
  const [zipSearchProspection, setZipSearchProspection] = useState('');
  const [filteredZipcodesProspection, setFilteredZipcodesProspection] = useState([]);
  const [accessibleZipcodesProspection, setAccessibleZipcodesProspection] = useState([]);
  const [zipLabelsProspection, setZipLabelsProspection] = useState({});
  const [cityOptions, setCityOptions] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);  // Multi-select categories
  const [selectedDays, setSelectedDays] = useState('15');  // Date range filter (15/30/90/all)
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [postbirdStatus, setPostbirdStatus] = useState('not_contacted'); // Renamed from contactStatus
  const [prospectionStatus, setProspectionStatus] = useState('');
  
  // Dialogs
  const [createCampaignOpen, setCreateCampaignOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  
  // Send dialog state
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);
  
  // Initialize delayPayment based on module access
  const canAccessDelayPayment = useMemo(() => hasModuleAccess(user, 'postbirdDelayPayment'), [user]);
  
  const [sendOptions, setSendOptions] = useState({
    color: false,
    twoSided: false,
    registered: false,
    delayPayment: false // Will be updated by useEffect
  });
  
  // Update delayPayment when user or module access changes
  useEffect(() => {
    setSendOptions(prev => ({
      ...prev,
      delayPayment: canAccessDelayPayment ? true : false
    }));
  }, [canAccessDelayPayment]);
  
  // Confirmation popup state
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  
  // Template dialog state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    file: null,
    color: 'zwart_wit',
    sides: 'enkelzijdig',
    registered: false,
    isDefault: false
  });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [uploading, setUploading] = useState(false);

  const canAccessOOWoningen = useMemo(() => hasModuleAccess(user, 'oo_woningen'), [user]);

  useEffect(() => {
    fetchSummary();
    fetchCampaigns();
    fetchTemplates();
    if (canAccessOOWoningen) {
      fetchOoFavorites();
    } else {
      setOoFavorites([]);
      setSelectedOoListings(new Set());
    }
    fetchCredits();
  }, [canAccessOOWoningen]);

  useEffect(() => {
    let isMounted = true;
    const loadCities = async () => {
      setCitiesLoading(true);
      try {
        const options = await fetchCityOptionsFromApi();
        if (isMounted) {
          setCityOptions(options);
        }
      } catch (error) {
        console.error('Failed to load city options:', error);
      } finally {
        if (isMounted) {
          setCitiesLoading(false);
        }
      }
    };
    loadCities();
    return () => {
      isMounted = false;
    };
  }, []);


  // Fetch accessible zipcodes for Prospection when user is available
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/location/users/${user.id}/access-summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const zips = res.data?.accessible_zipcodes || [];
        setAccessibleZipcodesProspection([...new Set(zips)]);
        setZipLabelsProspection(res.data?.zipcode_labels || {});
      } catch (e) {
        setAccessibleZipcodesProspection([]);
        setZipLabelsProspection({});
      }
      const groups = await fetchLeadCategoryGroups();
      setCategoryGroups(groups);
    })();
  }, [user?.id]);

  // Auto-select default template when templates are loaded
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplate) {
      const defaultTemplate = templates.find(t => t.type === 'post' && t.is_default);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate.id);
        // Auto-apply template settings
        applyTemplateSettings(defaultTemplate);
      }
    }
  }, [templates, selectedTemplate]);

  // Function to apply template settings when template is selected
  const applyTemplateSettings = (template) => {
    if (template) {
      setSendOptions(prev => ({
        ...prev,
        color: template.color === 'kleur',
        twoSided: template.sides === 'dubbelzijdig',
        registered: template.registered
      }));
    }
  };

  // Handle template selection change
  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      applyTemplateSettings(template);
    }
  };

  useEffect(() => {
    setPage(1);
    setTotalMatchingLeads(0);
  }, [searchTerm, selectedZipcodes, selectedCategories, selectedDays, selectedStatus, postbirdStatus]);

  useEffect(() => {
    setLoading(true);
    fetchLeads(page, pageSize);
    fetchCampaignFilteredCount();
    if (canAccessOOWoningen) {
      fetchOoFavorites();
    }
  }, [page, pageSize, searchTerm, selectedZipcodes, selectedCategories, selectedDays, selectedStatus, postbirdStatus, canAccessOOWoningen]);

  // Auto-refresh credits when window becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchCredits();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/leads/stats/summary`, {
        headers: getAuthHeaders(),
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const appendCampaignFilters = (params) => {
    if (selectedZipcodes && selectedZipcodes.length > 0) {
      selectedZipcodes.forEach((z) => params.append('zipcodes', z));
    }
    if (selectedCategories && selectedCategories.length > 0) {
      selectedCategories.forEach((c) => params.append('categories', c));
    }
    if (selectedDays && selectedDays !== 'all') params.set('days_back', selectedDays);
    if (selectedStatus && selectedStatus !== 'all') params.set('status', selectedStatus);
    if (postbirdStatus && postbirdStatus !== 'all') params.set('contact_status', postbirdStatus);
    if (searchTerm) params.set('search', searchTerm);
    params.set('favorites_only', 'true');
  };

  const buildCampaignFilterParams = () => {
    const params = new URLSearchParams();
    appendCampaignFilters(params);
    return params;
  };

  const buildCampaignLeadParams = (skip, limit) => {
    const params = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
    });
    appendCampaignFilters(params);
    return params;
  };

  const fetchCampaignFilteredCount = async () => {
    try {
      const params = buildCampaignFilterParams();
      const response = await axios.get(`${API}/leads/stats/count?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      setCampaignFilteredCount(response.data?.total ?? 0);
    } catch (error) {
      console.error('Failed to fetch campaign filtered count:', error);
      setCampaignFilteredCount(0);
    }
  };

  const fetchAllCampaignLeadIds = async () => {
    const ids = [];
    const batchSize = 100;
    let skip = 0;

    while (true) {
      const params = buildCampaignLeadParams(skip, batchSize);
      const response = await axios.get(`${API}/leads?${params.toString()}`);
      const batch = response.data || [];
      batch.forEach((lead) => ids.push(lead.id));
      if (batch.length < batchSize) break;
      skip += batchSize;
    }

    return ids;
  };

  const fetchLeads = async (pageArg = page, pageSizeArg = pageSize) => {
    try {
      setLeadsFetchError('');
      const params = buildCampaignLeadParams((pageArg - 1) * pageSizeArg, pageSizeArg);
      const response = await axios.get(`${API}/leads?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      setLeads(response.data);
      setHasMoreLeads(response.data.length === pageSizeArg);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 403) {
        setLeads([]);
        setLeadsFetchError(
          typeof detail === 'string' && detail
            ? detail
            : 'Geen toegang tot campagne-leads (403). Controleer je abonnement of log opnieuw in.'
        );
      } else if (status === 401) {
        setLeadsFetchError('Sessie verlopen. Log opnieuw in.');
      } else {
        setLeadsFetchError('Campagne-leads konden niet geladen worden. Probeer opnieuw.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/prospection/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/prospection/templates`, {
        params: { include_pdf: false },
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchOoFavorites = async () => {
    if (!canAccessOOWoningen) {
      setOoFavorites([]);
      setSelectedOoListings(new Set());
      return;
    }
    try {
      setOoLoading(true);
      const response = await axios.get(`${API}/oo/listings`, {
        params: buildDaysBackParams(selectedDays, {
          favorites_only: true,
          limit: 500,
          status: 'all',
        }),
      });
      const items = response.data?.items || [];
      setOoFavorites(items);
      setSelectedOoListings((prev) => {
        const next = new Set();
        items.forEach((item) => {
          if (prev.has(item.poiid)) {
            next.add(item.poiid);
          }
        });
        return next;
      });
    } catch (error) {
      console.error('Failed to fetch OO-woningen favorieten:', error);
      setOoFavorites([]);
    } finally {
      setOoLoading(false);
    }
  };

  const fetchCredits = async () => {
    try {
      setCreditsLoading(true);
      const response = await axios.get(`${API}/credits/balance`);
      setCredits(response.data.current_balance);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    } finally {
      setCreditsLoading(false);
    }
  };

  const handleSendDocuments = async () => {
    if (!selectedTemplate) {
      alert('Selecteer eerst een template');
      return;
    }

    if (totalSelectedRecipients === 0) {
      alert('Selecteer eerst ontvangers om naar te versturen');
      return;
    }

    // Show confirmation popup first
    setConfirmSendOpen(true);
  };

  const confirmSendDocuments = async () => {
    setSending(true);
    setConfirmSendOpen(false);
    
    try {
      const leadIds = Array.from(selectedLeads);
      const ooIds = Array.from(selectedOoListings);
      const response = await axios.post(`${API}/prospection/send-document`, {
        lead_ids: leadIds,
        oo_ids: ooIds,
        template_id: selectedTemplate,
        color: sendOptions.color,
        two_sided: sendOptions.twoSided,
        registered: sendOptions.registered,
        delay_payment: sendOptions.delayPayment
      });

      if (response.data.success) {
        const totalRecipients = response.data.total_recipients ?? response.data.total_leads ?? totalSelectedRecipients;
        const successfulLeads = response.data.successful_leads ?? response.data.successful_sends ?? 0;
        const successfulOo = response.data.successful_oo ?? 0;
        const successBreakdown = [];
        if (successfulLeads) successBreakdown.push(`${successfulLeads} lead(s)`);
        if (successfulOo) successBreakdown.push(`${successfulOo} OO-woning(en)`);
        const breakdownText = successBreakdown.length ? ` (${successBreakdown.join(', ')})` : '';

        alert(`Succesvol verstuurd: ${response.data.successful_sends || 0} van ${totalRecipients} ontvangers${breakdownText}`);
        setSendDialogOpen(false);
        setSelectedLeads(new Set());
        setSelectedOoListings(new Set());
        setSelectedTemplate('');
        // Refresh credits and leads after successful send
        fetchCredits();
        fetchLeads();
        fetchCampaigns();
        if (canAccessOOWoningen) {
          fetchOoFavorites();
        }
      } else {
        alert('Er is een fout opgetreden bij het versturen');
      }
    } catch (error) {
      console.error('Failed to send documents:', error);
      alert('Er is een fout opgetreden bij het versturen');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Alleen PDF bestanden zijn toegestaan');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        alert('Bestand is te groot. Maximum 10MB toegestaan');
        return;
      }
      setTemplateForm(prev => ({ ...prev, file }));
    }
  };

  const handleTemplateSubmit = async () => {
    if (!templateForm.name.trim()) {
      alert('Voer een template naam in');
      return;
    }
    if (!templateForm.file) {
      alert('Selecteer een PDF bestand');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', templateForm.name);
      formData.append('file', templateForm.file);
      formData.append('type', 'post');
      formData.append('color', templateForm.color);
      formData.append('sides', templateForm.sides);
      formData.append('registered', templateForm.registered);
      formData.append('is_default', templateForm.isDefault);

      const response = await axios.post(`${API}/prospection/templates`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        alert('Template succesvol opgeslagen!');
        setTemplateDialogOpen(false);
        setTemplateForm({
          name: '',
          file: null,
          color: 'zwart_wit',
          sides: 'enkelzijdig',
          registered: false,
          isDefault: false
        });
        fetchTemplates(); // Refresh templates list
      }
    } catch (error) {
      console.error('Failed to upload template:', error);
      console.error('Error response:', error.response);
      console.error('Error details:', error.response?.data);
      alert(`Er is een fout opgetreden bij het uploaden van het template: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Template management handlers
  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      file: null,
      color: template.color,
      sides: template.sides,
      registered: template.registered,
      isDefault: template.is_default
    });
    setTemplateDialogOpen(true);
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Weet je zeker dat je het template "${template.name}" wilt verwijderen?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/prospection/templates/${template.id}`);
      alert('Template succesvol verwijderd!');
      fetchTemplates(); // Refresh templates list
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert(`Er is een fout opgetreden bij het verwijderen van het template: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handlePreviewTemplate = async (template) => {
    try {
      let pdfBase64 = template.pdf_base64;
      if (!pdfBase64) {
        const response = await axios.get(`${API}/prospection/templates/${template.id}`);
        pdfBase64 = response.data?.pdf_base64;
      }

      if (pdfBase64) {
        try {
          const base64Length = pdfBase64.length;
        
        // Check if data looks truncated (65,535 is the TEXT field limit)
        if (base64Length === 65535) {
          alert('PDF data appears to be truncated. Please re-upload the template.');
          return;
        }
        
        // Check if base64 is valid
        try {
          atob(pdfBase64.substring(0, 100)); // Test first 100 chars
        } catch (e) {
          console.error('Invalid base64 data:', e);
          alert('Invalid PDF data. Please re-upload the template.');
          return;
        }
        
        // Convert base64 to blob and create blob URL (browsers handle this better than data URLs)
        const binaryString = atob(pdfBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        
        console.log('Blob URL created:', blobUrl);
        
        // Try to open blob URL in new window
        const pdfWindow = window.open(blobUrl, '_blank');
        
        if (!pdfWindow || pdfWindow.closed) {
          // If popup was blocked, try opening in same window with iframe
          console.log('Popup blocked, trying iframe method...');
          
          // Create a modal with iframe
          const modal = document.createElement('div');
          modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.8);
            z-index: 10000;
            display: flex;
            justify-content: center;
            align-items: center;
          `;
          
          const iframe = document.createElement('iframe');
          iframe.src = blobUrl;
          iframe.style.cssText = `
            width: 90%;
            height: 90%;
            border: none;
            background: white;
          `;
          
          const closeButton = document.createElement('button');
          closeButton.innerHTML = '✕';
          closeButton.style.cssText = `
            position: absolute;
            top: 10px;
            right: 10px;
            background: #ff4444;
            color: white;
            border: none;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            font-size: 20px;
            cursor: pointer;
            z-index: 10001;
          `;
          
          closeButton.onclick = () => {
            document.body.removeChild(modal);
            URL.revokeObjectURL(blobUrl);
          };
          
          modal.appendChild(iframe);
          modal.appendChild(closeButton);
          document.body.appendChild(modal);
          
          // Clean up blob URL after modal is closed
          setTimeout(() => {
            if (!document.body.contains(modal)) {
              URL.revokeObjectURL(blobUrl);
            }
          }, 10000);
          
        } else {
          // Clean up the blob URL after a delay
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 5000);
        }
        
      } catch (error) {
        console.error('Error creating PDF preview:', error);
        alert('Er is een fout opgetreden bij het openen van de PDF: ' + error.message);
      }
      } else {
        alert('Geen PDF beschikbaar voor dit template');
      }
    } catch (error) {
      console.error('Failed to load template preview:', error);
      alert('Kon template niet laden voor preview.');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    if (!templateForm.name.trim()) {
      alert('Voer een template naam in');
      return;
    }

    setUploading(true);
    try {
      const updateData = {
        name: templateForm.name,
        color: templateForm.color,
        sides: templateForm.sides,
        registered: templateForm.registered,
        is_default: templateForm.isDefault
      };

      await axios.put(`${API}/prospection/templates/${editingTemplate.id}`, updateData);

      // If a new file is selected, upload it
      if (templateForm.file) {
        const formData = new FormData();
        formData.append('file', templateForm.file);
        await axios.post(`${API}/prospection/templates/${editingTemplate.id}/upload-pdf`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      alert('Template succesvol bijgewerkt!');
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      setTemplateForm({
        name: '',
        file: null,
        color: 'zwart_wit',
        sides: 'enkelzijdig',
        registered: false,
        isDefault: false
      });
      fetchTemplates(); // Refresh templates list
    } catch (error) {
      console.error('Failed to update template:', error);
      alert(`Er is een fout opgetreden bij het bijwerken van het template: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleLeadSelection = (leadId, checked) => {
    const newSelected = new Set(selectedLeads);
    if (checked) {
      newSelected.add(leadId);
    } else {
      newSelected.delete(leadId);
    }
    setSelectedLeads(newSelected);
  };

  const handleSelectAll = async (checked) => {
    if (!checked) {
      setSelectedLeads(new Set());
      setTotalMatchingLeads(0);
      return;
    }

    setSelectingAll(true);
    try {
      const allIds = await fetchAllCampaignLeadIds();
      setSelectedLeads(new Set(allIds));
      setTotalMatchingLeads(allIds.length);
    } catch (error) {
      console.error('Failed to select all campaign leads:', error);
      alert('Kon niet alle campagne-leads selecteren. Probeer opnieuw.');
    } finally {
      setSelectingAll(false);
    }
  };

  const handleOoSelectAll = (checked) => {
    if (checked) {
      setSelectedOoListings(new Set(ooFavorites.map((item) => item.poiid)));
    } else {
      setSelectedOoListings(new Set());
    }
  };

  const handleOoSelection = (poiid, checked) => {
    setSelectedOoListings((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(poiid);
      } else {
        next.delete(poiid);
      }
      return next;
    });
  };

  const markOoListingAsRead = async (listing) => {
    if (!listing || listing.status === 'gelezen' || listing.status === 'interessant' || listing.status === 'gecontacteerd') {
      return;
    }
    try {
      await axios.patch(`${API}/oo/listings/${listing.poiid}`, { status: 'gelezen' });
      setOoFavorites((prev) =>
        prev.map((item) =>
          item.poiid === listing.poiid ? { ...item, status: 'gelezen' } : item,
        ),
      );
      setSelectedOOWoning((prev) =>
        prev && prev.poiid === listing.poiid ? { ...prev, status: 'gelezen' } : prev,
      );
    } catch (error) {
      console.error('Failed to mark OO-woning as gelezen:', error);
    }
  };

  const handleOoToggleFavoriteButton = async (listing, nextValue) => {
    if (!listing) return;
    try {
      await axios.patch(`${API}/oo/listings/${listing.poiid}`, { is_favorite: nextValue });
      setOoFavorites((prev) =>
        prev.map((item) =>
          item.poiid === listing.poiid ? { ...item, is_favorite: nextValue } : item,
        ),
      );
      setSelectedOOWoning((prev) =>
        prev && prev.poiid === listing.poiid ? { ...prev, is_favorite: nextValue } : prev,
      );
    } catch (error) {
      console.error('Failed to toggle OO favorite:', error);
    }
  };

  const handleOoToggleStatus = async (listing) => {
    if (!listing) return;
    const isInteressant = listing.status === 'interessant';
    const nextStatus = isInteressant ? 'gelezen' : 'interessant';
    try {
      await axios.patch(`${API}/oo/listings/${listing.poiid}`, { status: nextStatus });
      setOoFavorites((prev) =>
        prev.map((item) =>
          item.poiid === listing.poiid ? { ...item, status: nextStatus } : item,
        ),
      );
      setSelectedOOWoning((prev) =>
        prev && prev.poiid === listing.poiid ? { ...prev, status: nextStatus } : prev,
      );
      // Refresh favorites to ensure data is in sync
      if (canAccessOOWoningen) {
        fetchOoFavorites();
      }
    } catch (error) {
      console.error('Failed to toggle OO status:', error);
    }
  };

  const handleOoRowClick = (listing) => {
    setSelectedOOWoning(listing);
    setOoSheetOpen(true);
    markOoListingAsRead(listing);
  };

  const handleExportCSV = async () => {
    try {
      const leadIds = Array.from(selectedLeads).join(',');
      const params = new URLSearchParams();
      params.set('campaign_type', activeTab);
      if (leadIds) params.set('lead_ids', leadIds);
      if (selectedZipcodes && selectedZipcodes.length > 0) {
        selectedZipcodes.forEach(z => params.append('zipcodes', z));
      }
      if (selectedCategories && selectedCategories.length > 0) {
        selectedCategories.forEach((c) => params.append('categories', c));
      }
      if (selectedDays && selectedDays !== 'all') params.set('days_back', selectedDays);
      if (selectedStatus && selectedStatus !== 'all') params.set('status', selectedStatus);

      const response = await axios.get(`${API}/prospection/leads/export?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  };

  // Build zipcode options from city API and user access summary (no bulk lead fetch)
  const availableZipcodesProspection = useMemo(() => {
    const dbOptions = filterCityOptionsByAccess(cityOptions, accessibleZipcodesProspection);
    if (dbOptions.length > 0) {
      return dbOptions;
    }
    if (accessibleZipcodesProspection.length > 0) {
      return sortZipOptions(accessibleZipcodesProspection.map((zip) => ({
        zipcode: zip,
        gemeente: zipLabelsProspection[zip] || 'Onbekend',
        searchValue: buildSearchValue(zip, zipLabelsProspection[zip] || 'Onbekend'),
      })));
    }
    return [];
  }, [cityOptions, accessibleZipcodesProspection, zipLabelsProspection]);

  const searchQueryProspection = normalizeSearchValue(zipSearchProspection);
  useEffect(() => {
    const searchQueryProspection = normalizeSearchValue(zipSearchProspection);
    if (!searchQueryProspection) {
      setFilteredZipcodesProspection(availableZipcodesProspection);
      return;
    }
    const next = availableZipcodesProspection.filter(({ zipcode, gemeente, searchValue }) => {
      const target = searchValue ?? buildSearchValue(zipcode, gemeente);
      return target.includes(searchQueryProspection);
    });
    setFilteredZipcodesProspection(next);
  }, [zipSearchProspection, availableZipcodesProspection]);
  
  const filteredLeads = leads;
  const campaignLeadCount = campaignFilteredCount;
  const allCampaignLeadsSelected = totalMatchingLeads > 0 && selectedLeads.size === totalMatchingLeads;
  const canPrevPage = page > 1;
  const canNextPage = hasMoreLeads && page * pageSize < campaignFilteredCount;
  const filteredOoFavorites = useMemo(() => {
    let list = ooFavorites.map((item) => {
      const hasPostbird = Boolean(item?.postbird_id);
      const effectiveStatus =
        item?.status === 'gecontacteerd' || hasPostbird
          ? 'gecontacteerd'
          : item?.status || 'new';
      return { ...item, effectiveStatus, hasPostbird };
    });

    if (selectedStatus !== 'all') {
      list = list.filter((item) => {
        if (selectedStatus === 'gecontacteerd') {
          return item.effectiveStatus === 'gecontacteerd';
        }
        return item.effectiveStatus === selectedStatus;
      });
    }

    if (postbirdStatus === 'contacted') {
      list = list.filter((item) => item.hasPostbird);
    } else if (postbirdStatus === 'not_contacted') {
      list = list.filter((item) => !item.hasPostbird);
    }

    return list;
  }, [ooFavorites, postbirdStatus, selectedStatus]);
  const totalSelectedRecipients = selectedLeads.size + selectedOoListings.size;

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader 
        user={user} 
        currentPage="prospectie"
        onNavigate={(path) => window.location.href = path}
        onLogout={logout}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Campagnes </h2>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Geselecteerde ontvangers</p>
                  <p className="text-2xl font-bold text-gray-900">{totalSelectedRecipients}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Leads: {selectedLeads.size} • OO-woningen: {selectedOoListings.size}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Gecontacteerd</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.status_counts?.gecontacteerd || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <MailOpen className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Verzonden campagnes</p>
                  <p className="text-2xl font-bold text-gray-900">{campaigns.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Zap className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Credits</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {creditsLoading ? '...' : credits}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchCredits}
                  disabled={creditsLoading}
                  className="p-2"
                >
                  <RefreshCw className={`w-4 h-4 ${creditsLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  window.location.href = '/profile#credits';
                }}
                className="w-full text-sm"
              >
                Credits kopen
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Campaign Type Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full grid-cols-2">
           {/* <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              E-mailcampagnes
            </TabsTrigger>*/}
            <TabsTrigger value="post" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Postcampagnes
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Templates ({templates.filter(t => t.type === 'post').length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="space-y-6">
            {/* Email Campaign Content */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>E-mailcampagne beheer</CardTitle>
                    <CardDescription>
                      Selecteer leads en exporteer naar CSV voor e-mailcampagnes
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleExportCSV}
                      disabled={selectedLeads.size === 0}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV ({selectedLeads.size})
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          </TabsContent>

          <TabsContent value="post" className="space-y-6">
            {/* Post Campaign Content */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Postcampagne beheer</CardTitle>
                    <CardDescription>
                      Beheer templates en verstuur brieven via Postbird API
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                   {/*} <Button
                      variant="outline"
                      onClick={() => setTemplateDialogOpen(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nieuw Template
                    </Button>*/}
                    <Button
                      onClick={() => setSendDialogOpen(true)}
                      disabled={totalSelectedRecipients === 0}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Verstuur Post ({totalSelectedRecipients})
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          </TabsContent>

          <TabsContent value="templates" className="space-y-6">
            {/* Template Management Content */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Template Beheer ({templates.filter(t => t.type === 'post').length})</CardTitle>
                    <CardDescription>
                      Beheer uw post templates - bewerk, verwijder en bekijk templates
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setTemplateDialogOpen(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nieuw Template
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {templates.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Geen templates</h3>
                    <p className="text-gray-500 mb-4">U heeft nog geen templates aangemaakt.</p>
                    <Button onClick={() => setTemplateDialogOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Eerste Template Aanmaken
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templates.filter(t => t.type === 'post').map(template => (
                      <Card key={template.id} className="relative">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-lg">{template.name}</CardTitle>
                              {template.is_default && (
                                <Badge variant="default" className="mt-1">Standaard</Badge>
                              )}
                            </div>
                            <div className="flex items-center space-x-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePreviewTemplate(template)}
                                title="Bekijk template"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditTemplate(template)}
                                title="Bewerk template"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteTemplate(template)}
                                title="Verwijder template"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="space-y-2 text-sm text-gray-600">
                            <div className="flex items-center justify-between">
                              <span>Type:</span>
                              <Badge variant="outline">{template.type}</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Kleur:</span>
                              <span className="capitalize">{template.color.replace('_', ' ')}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Zijden:</span>
                              <span className="capitalize">{template.sides}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>Aangemaakt:</span>
                              <span>{new Date(template.created_at).toLocaleDateString('nl-NL')}</span>
                            </div>
                            {template.pdf_filename && (
                              <div className="flex items-center justify-between">
                                <span>PDF:</span>
                                <span className="text-green-600">✓ Geüpload</span>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

              <div className="space-y-2 relative">
                <Label>Postcodes</Label>
                <button
                  type="button"
                  onClick={() => setZipDropdownOpen(!zipDropdownOpen)}
                  className="w-full px-3 py-2 h-12 text-base border border-gray-300 rounded-md text-left focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {selectedZipcodes.length === 0 ? 'Alle postcodes' : `${selectedZipcodes.length} geselecteerd`}
                </button>
                {zipDropdownOpen && (
                  <div className="absolute z-50 mt-2 w-full max-h-64 overflow-auto bg-white border border-gray-200 rounded-md shadow">
                    <div className="p-2">
                      <div className="mb-2">
                        <Input
                          type="text"
                          placeholder="Zoek postcode of gemeente..."
                          value={zipSearchProspection}
                          onChange={(e) => setZipSearchProspection(e.target.value)}
                          className="w-full h-10 text-base"
                        />
                      </div>
                      <div className="flex items-center gap-2 px-2 py-1">
                        <input
                          id="zip-all-prospection"
                          type="checkbox"
                          checked={filteredZipcodesProspection.length > 0 && filteredZipcodesProspection.every(z => selectedZipcodes.includes(z.zipcode))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedZipcodes(prev => [...new Set([...prev, ...filteredZipcodesProspection.map(z => z.zipcode)])]);
                            } else {
                              setSelectedZipcodes(prev => prev.filter(z => !filteredZipcodesProspection.some(f => f.zipcode === z)));
                            }
                          }}
                        />
                        <label htmlFor="zip-all-prospection" className="text-sm text-gray-700">Alles selecteren</label>
                      </div>
                      <div className="border-t my-1" />
                      {filteredZipcodesProspection.length === 0 && (
                        <div className="px-2 py-1 text-sm text-gray-500">
                          {citiesLoading ? 'Postcodes laden...' : 'Geen postcodes gevonden'}
                        </div>
                      )}
                      {filteredZipcodesProspection.map(({ zipcode, gemeente }, index) => (
                        <label key={`${zipcode}-${index}`} className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={selectedZipcodes.includes(zipcode)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedZipcodes(prev => [...new Set([...prev, zipcode])]);
                              } else {
                                setSelectedZipcodes(prev => prev.filter(z => z !== zipcode));
                              }
                            }}
                          />
                          <span className="text-sm text-gray-700">{gemeente} ({zipcode})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Periode (aanvraagdatum)</Label>
                <Select value={selectedDays} onValueChange={setSelectedDays}>
                  <SelectTrigger className="h-12 text-base">
                    <SelectValue placeholder="Laatste 15 dagen" />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_RANGE_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Categorie</Label>
                <CategoryMultiSelect
                  groups={categoryGroups}
                  selected={selectedCategories}
                  onChange={setSelectedCategories}
                />
                {categoryGroups.length === 0 && (
                  <p className="text-xs text-gray-500">Geen categorierechten toegewezen</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Lead Status</Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="h-12 text-base">
                    <SelectValue placeholder="Alle statussen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle statussen</SelectItem>
                    <SelectItem value="new">Nieuw</SelectItem>
                    <SelectItem value="interessant">Favorieten</SelectItem>
                    <SelectItem value="gelezen">Gelezen</SelectItem>                    
                    <SelectItem value="gecontacteerd">Gecontacteerd</SelectItem>
                    {/*<SelectItem value="offerte">Offerte</SelectItem>
                    <SelectItem value="lopend">Lopend</SelectItem>
                    <SelectItem value="gearchiveerd">Gearchiveerd</SelectItem>*/}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Postbird Status</Label>
                <Select value={postbirdStatus} onValueChange={setPostbirdStatus}>
                  <SelectTrigger className="h-12 text-base">
                    <SelectValue placeholder="Alle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="contacted">Verstuurd via Postbird</SelectItem>
                    <SelectItem value="not_contacted">Nog niet verstuurd</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Full-width search at the bottom of filters */}
              <div className="space-y-2 lg:col-span-4">
                <Label>Zoeken</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Zoek in leads..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-12 text-base"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* OO-woningen Favorites */}
        {canAccessOOWoningen && (
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-start gap-3">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setOoCardCollapsed((prev) => !prev)}
                aria-label={ooCardCollapsed ? 'Kaart uitklappen' : 'Kaart inklappen'}
              >
                <ChevronDown className={`h-5 w-5 transition-transform ${ooCardCollapsed ? '-rotate-90' : 'rotate-0'}`} />
              </Button>
              <div>
                <CardTitle>Leads Ongeschikt Onbewoonbaar ({filteredOoFavorites.length})</CardTitle>
                <CardDescription>Geselecteerde OO-woningen klaar voor verzending via Postbird</CardDescription>
              </div>
            </div>
          </CardHeader>
          {!ooCardCollapsed && (
            <CardContent>
            {ooLoading ? (
              <div className="text-center py-8 text-gray-500">Favorieten laden...</div>
            ) : filteredOoFavorites.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Geen favoriete OO-woningen gevonden voor deze filter. Pas de filters aan of voeg nieuwe favorieten toe in het OO-woningen module.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center space-x-3 pb-4 border-b border-gray-200">
                  <Checkbox
                    checked={
                      filteredOoFavorites.length > 0 &&
                      filteredOoFavorites.every((item) => selectedOoListings.has(item.poiid))
                    }
                    onCheckedChange={(checked) => handleOoSelectAll(checked)}
                  />
                  <Label
                    className="text-sm font-medium text-gray-700 cursor-pointer"
                    onClick={() =>
                      handleOoSelectAll(
                        !(
                          filteredOoFavorites.length > 0 &&
                          filteredOoFavorites.every((item) => selectedOoListings.has(item.poiid))
                        ),
                      )
                    }
                  >
                    Selecteer alles ({selectedOoListings.size} geselecteerd)
                  </Label>
                </div>

                  <div className="space-y-4">
                    {filteredOoFavorites.map((listing) => {
                      const isSelected = selectedOoListings.has(listing.poiid);
                      return (
                        <div
                          key={listing.poiid}
                          className={`border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${isSelected ? 'bg-emerald-50 border-emerald-200' : ''}`}>
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => {
                                handleOoSelection(listing.poiid, checked);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-semibold text-lg text-gray-900">
                                  {listing.naam || `OO-woning ${listing.poiid}`}
                                </h3>
                                <Badge variant="outline">OO-woning</Badge>
                                {listing.hasPostbird && (
                                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 flex items-center gap-1">
                                    <MailOpen className="w-3 h-3" />
                                    Gecontacteerd
                                  </Badge>
                                )}
                                <StatusBadge status={listing.status} />
                              </div>
                              <div className="text-sm text-gray-600 flex flex-wrap gap-2">
                                <span>
                                  {listing.straat} {listing.huisnummer}
                                  {listing.busnummer ? ` bus ${listing.busnummer}` : ''}
                                </span>
                                <span>•</span>
                                <span>{listing.postcode} {listing.gemeente}</span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Laatst gezien: {listing.last_seen_at ? new Date(listing.last_seen_at).toLocaleDateString('nl-NL') : 'Onbekend'}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                             {/* <Button
                                variant="ghost"
                                size="icon"
                                className={`h-8 w-8 rounded-full hover:bg-gray-100 ${
                                  listing.status === 'interessant'
                                    ? 'text-yellow-500 bg-red-50'
                                    : 'text-gray-400 hover:text-red-500'
                                }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleOoToggleStatus(listing);
                                }}
                              >
                                <Star className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={`h-8 w-8 rounded-full hover:bg-gray-100 ${
                                  listing.is_favorite ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:text-red-500'
                                }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleOoToggleFavoriteButton(listing, !listing.is_favorite);
                                }}
                              >
                                <Mail className="w-4 h-4" style={{ width: '25px', height: '25px' }} />
                              </Button>*/}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      handleOoRowClick(listing)
                                    }}
                                    className="mt-1"
                                  >
                                  <Eye className="w-4 h-4" />
                                </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          )}
          </Card>
        )}

        {/* Leads List */}
        <Card>
          <CardHeader>
            <CardTitle>
              Leads verguningen ({campaignLeadCount})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leadsFetchError ? (
              <div className="text-center py-8">
                <div className="text-red-600 font-medium mb-2">{leadsFetchError}</div>
                <p className="text-sm text-gray-500">
                  Controleer je abonnement of log opnieuw in als je een 403-fout ziet.
                </p>
              </div>
            ) : loading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Leads laden...</div>
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Geen leads gevonden</div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Select All Checkbox */}
                <div className="flex items-center space-x-3 pb-4 border-b border-gray-200">
                  <Checkbox
                    checked={allCampaignLeadsSelected}
                    disabled={selectingAll}
                    onCheckedChange={(checked) => handleSelectAll(checked)}
                    className="mt-1"
                  />
                  <Label
                    className={`text-sm font-medium text-gray-700 ${selectingAll ? 'opacity-60' : 'cursor-pointer'}`}
                    onClick={() => {
                      if (!selectingAll) {
                        handleSelectAll(!allCampaignLeadsSelected);
                      }
                    }}
                  >
                    {selectingAll
                      ? 'Alle campagne-leads selecteren...'
                      : `Selecteer alles (${selectedLeads.size} geselecteerd)`}
                  </Label>
                </div>

                {filteredLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start space-x-4">
                      <Checkbox
                        checked={selectedLeads.has(lead.id)}
                        onCheckedChange={(checked) => handleLeadSelection(lead.id, checked)}
                        className="mt-1"
                      />
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg text-gray-900">
                            {lead.title}
                          </h3>
                          <StatusBadge status={lead.status} />
                          {lead.postbird_id && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              <MailOpen className="w-3 h-3 mr-1" />
                              Gecontacteerd
                            </Badge>
                          )}
                          {lead.is_favorite && (
                            <Mail className="w-4 h-4 text-red-500 bg-red-50 rounded p-0.5" />
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-2">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{lead?.street} {lead?.number}, {lead?.zipcode} {lead?.municipality}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Building className="w-4 h-4" />
                            <span>{lead.permit_type}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(lead.datum_aanvraag).toLocaleDateString('nl-NL')}</span>
                          </div>
                        </div>
                        
                        {lead.eigenaar_info && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-2">
                            {lead.eigenaar_info.name && (
                              <div className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                <span>{lead.eigenaar_info.name}</span>
                              </div>
                            )}
                            {lead.eigenaar_info.email && (
                              <div className="flex items-center gap-1">
                                <Mail className="w-4 h-4" />
                                <span>{lead.eigenaar_info.email}</span>
                              </div>
                            )}
                            {lead.eigenaar_info.phone && (
                              <div className="flex items-center gap-1">
                                <Phone className="w-4 h-4" />
                                <span>{lead.eigenaar_info.phone}</span>
                              </div>
                            )}
                            {lead.eigenaar_info.address && lead.eigenaar_info.address !== ", " && (
                              <div className="flex items-center gap-2 text-sm">
                                <MapPin className="w-4 h-4" />
                                <span>{lead.eigenaar_info.address}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {lead.description}
                        </p>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedLead(lead);
                          setSheetOpen(true);
                        }}
                        className="mt-1"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!loading && filteredLeads.length > 0 && (
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm text-gray-600">
                  Toont {(page - 1) * pageSize + 1}–{(page - 1) * pageSize + filteredLeads.length}
                  {campaignLeadCount ? ` van ${campaignLeadCount} campagne-leads` : ''}
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => {
                      setPageSize(Number(v));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue placeholder={`${pageSize} / pagina`} />
                    </SelectTrigger>
                    <SelectContent>
                      {[25, 50, 100].map((s) => (
                        <SelectItem key={s} value={String(s)}>{s} / pagina</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={!canPrevPage}>
                    « Eerste
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={!canPrevPage}>
                    ‹ Vorige
                  </Button>
                  <span className="text-sm text-gray-700 px-2">Pagina {page}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={!canNextPage}>
                    Volgende ›
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lead Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <LeadDetailSheet
          lead={selectedLead}
          onClose={() => setSheetOpen(false)}
          onUpdate={() => {
            fetchLeads();           
            setSheetOpen(false);
          }}
          fetchLeads={fetchLeads}
          fetchSummary={fetchSummary}
        />
      </Sheet>

      <Sheet
        open={ooSheetOpen}
        onOpenChange={(open) => {
          setOoSheetOpen(open);
          if (!open) {
            setSelectedOOWoning(null);
          }
        }}
      >
        <OOWoningDetailSheet
          listing={selectedOOWoning}
          onToggleFavorite={handleOoToggleFavoriteButton}
          onToggleStatus={handleOoToggleStatus}
        />
      </Sheet>

      {/* Template Creation Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={(open) => {
        setTemplateDialogOpen(open);
        if (!open) {
          // Reset form when dialog is closed
          setTemplateForm({
            name: '',
            file: null,
            color: 'zwart_wit',
            sides: 'enkelzijdig',
            registered: false,
            isDefault: false
          });
          setEditingTemplate(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Bewerk Template' : 'Nieuw Template'}</DialogTitle>
            <DialogDescription>
              {editingTemplate ? 'Bewerk uw template instellingen' : 'Maak een nieuw template voor postcampagnes'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template naam</Label>
              <Input 
                placeholder="Bijv. Standaard flyer" 
                value={templateForm.name}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>PDF Template</Label>
              {editingTemplate && editingTemplate.pdf_filename && !templateForm.file && (
                <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    Huidige PDF: {editingTemplate.pdf_filename}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Selecteer een nieuw bestand om te vervangen
                  </p>
                </div>
              )}
              <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                templateForm.file 
                  ? 'border-green-300 bg-green-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}>
                <Upload className={`w-8 h-8 mx-auto mb-2 ${
                  templateForm.file ? 'text-green-600' : 'text-gray-400'
                }`} />
                <p className={`text-sm mb-2 ${
                  templateForm.file ? 'text-green-700' : 'text-gray-600'
                }`}>
                  {templateForm.file ? templateForm.file.name : 'Sleep hier uw PDF template of klik om te uploaden'}
                </p>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => document.getElementById('file-upload').click()}
                >
                  {templateForm.file ? 'Ander bestand selecteren' : 'Bestand selecteren'}
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kleur</Label>
                <Select 
                  value={templateForm.color}
                  onValueChange={(value) => setTemplateForm(prev => ({ ...prev, color: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zwart_wit">Zwart-wit</SelectItem>
                    <SelectItem value="kleur">Kleur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Zijden</Label>
                <Select 
                  value={templateForm.sides}
                  onValueChange={(value) => setTemplateForm(prev => ({ ...prev, sides: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="enkelzijdig">Enkelzijdig</SelectItem>
                    <SelectItem value="dubbelzijdig">Dubbelzijdig</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="registered" 
                checked={templateForm.registered}
                onCheckedChange={(checked) => setTemplateForm(prev => ({ ...prev, registered: checked }))}
                disabled={true}
              />
              <Label htmlFor="registered" className="text-gray-400">Aangetekend versturen (uitgeschakeld)</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="default" 
                checked={templateForm.isDefault}
                onCheckedChange={(checked) => setTemplateForm(prev => ({ ...prev, isDefault: checked }))}
              />
              <Label htmlFor="default">Als standaard template instellen</Label>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setTemplateDialogOpen(false)}
                disabled={uploading}
              >
                Annuleren
              </Button>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={editingTemplate ? handleUpdateTemplate : handleTemplateSubmit}
                disabled={uploading || !templateForm.name.trim() || (!editingTemplate && !templateForm.file)}
              >
                {uploading ? (editingTemplate ? 'Bijwerken...' : 'Uploaden...') : (editingTemplate ? 'Template bijwerken' : 'Template opslaan')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Post Campaign Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Postcampagne versturen</DialogTitle>
            <DialogDescription>
              Verstuur brieven naar {totalSelectedRecipients} geselecteerde ontvangers
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template selecteren</Label>
              <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Kies een template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.filter(t => t.type === 'post').map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} {template.is_default ? '(Standaard)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Separator />
            
            <div className="space-y-3">
              <Label>Afzendergegevens (uit uw profiel)</Label>
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-medium">Naam:</span> {user?.full_name || 'Niet ingevuld'}
                  </div>
                  <div>
                    <span className="font-medium">Bedrijf:</span> {user?.company_name || 'Niet ingevuld'}
                  </div>
                  <div>
                    <span className="font-medium">Straat:</span> {user?.company_street || 'Niet ingevuld'}
                  </div>
                  <div>
                    <span className="font-medium">Nummer:</span> {user?.company_number || 'Niet ingevuld'}
                  </div>
                  <div>
                    <span className="font-medium">Postcode:</span> {user?.company_postal_code || 'Niet ingevuld'}
                  </div>
                  <div>
                    <span className="font-medium">Stad:</span> {user?.company_city || 'Niet ingevuld'}
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Bewerk uw profiel om afzendergegevens aan te passen
                </p>
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-3">
              <Label>Verzendopties</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="color"
                    checked={sendOptions.color}
                    onChange={(e) => setSendOptions(prev => ({ ...prev, color: e.target.checked }))}
                    className="rounded"
                  />
                  <Label htmlFor="color" className="text-sm">Kleur</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="twoSided"
                    checked={sendOptions.twoSided}
                    onChange={(e) => setSendOptions(prev => ({ ...prev, twoSided: e.target.checked }))}
                    className="rounded"
                  />
                  <Label htmlFor="twoSided" className="text-sm">Dubbelzijdig</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="registered"
                    checked={sendOptions.registered}
                    onChange={(e) => setSendOptions(prev => ({ ...prev, registered: e.target.checked }))}
                    className="rounded"
                    disabled={true}
                  />
                  <Label htmlFor="registered" className="text-sm text-gray-400">Aangetekend (uitgeschakeld)</Label>
                </div>
                {canAccessDelayPayment && (
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="delayPayment"
                      checked={sendOptions.delayPayment}
                      onChange={(e) => setSendOptions(prev => ({ ...prev, delayPayment: e.target.checked }))}
                      className="rounded"
                    />
                    <Label htmlFor="delayPayment" className="text-sm">
                      Uitgestelde betaling (ontwikkeling)
                    </Label>
                  </div>
                )}
              </div>
              {canAccessDelayPayment && (
                <p className="text-xs text-gray-600 mt-2">
                  Uitgestelde betaling betekent dat de betaling niet automatisch wordt gedaan. 
                  U moet een aparte API-aanroep maken om te betalen en het document te verzenden.
                </p>
              )}
            </div>
            
            <Separator />
            
            <div className="bg-slate-100 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Geschatte kosten</span>
                <span className="text-lg font-bold text-emerald-600">
                  {totalSelectedRecipients} credits
                </span>
              </div>
              <p className="text-xs text-gray-600">
                1 credit per brief • Huidige saldo: {creditsLoading ? '...' : credits} credits
              </p>
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setSendDialogOpen(false)} disabled={sending}>
                Annuleren
              </Button>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700" 
                onClick={handleSendDocuments}
                disabled={sending || !selectedTemplate || totalSelectedRecipients === 0}
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Versturen...' : 'Campagne versturen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Send Dialog */}
      <Dialog open={confirmSendOpen} onOpenChange={setConfirmSendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bevestig Verzending</DialogTitle>
            <DialogDescription>
              Controleer de instellingen voordat je de campagne verstuurt
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Template Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Template Instellingen</h4>
              {(() => {
                const template = templates.find(t => t.id === selectedTemplate);
                return template ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Template:</span>
                      <span className="font-medium">{template.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Kleur:</span>
                      <span className="font-medium">{template.color === 'kleur' ? 'Kleur' : 'Zwart-wit'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Afdruk:</span>
                      <span className="font-medium">{template.sides === 'dubbelzijdig' ? 'Dubbelzijdig' : 'Enkelzijdig'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Aangetekend:</span>
                      <span className="font-medium">{template.registered ? 'Ja' : 'Nee'}</span>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>

            {/* Send Options */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Verzend Instellingen</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Kleur:</span>
                  <span className="font-medium">{sendOptions.color ? 'Kleur' : 'Zwart-wit'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Afdruk:</span>
                  <span className="font-medium">{sendOptions.twoSided ? 'Dubbelzijdig' : 'Enkelzijdig'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Aangetekend:</span>
                  <span className="font-medium">{sendOptions.registered ? 'Ja' : 'Nee'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Aantal ontvangers:</span>
                  <span className="font-medium">{totalSelectedRecipients}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setConfirmSendOpen(false)}
                disabled={sending}
              >
                Annuleren
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={confirmSendDocuments}
                disabled={sending}
              >
                {sending ? 'Versturen...' : 'Versturen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// MijnPerceel Module Component
const MijnPerceelModule = () => {
  const [mijnperceelProjects, setMijnperceelProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('unknown');
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [testMode, setTestMode] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGemeente, setSelectedGemeente] = useState('');
  
  const { user, logout } = useAuth();

  useEffect(() => {
    fetchMijnperceelProjects();
    testConnection();
  }, []);

  const testConnection = async () => {
    try {
      const response = await axios.get(`${API}/mijnperceel/test-connection`);
      setConnectionStatus(response.data.success ? 'connected' : 'error');
    } catch (error) {
      console.error('Connection test failed:', error);
      setConnectionStatus('error');
    }
  };

  const fetchMijnperceelProjects = async () => {
    try {
      const response = await axios.get(`${API}/mijnperceel/projects`);
      setMijnperceelProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch MijnPerceel projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (pageNumber = 0) => {
    setSyncing(true);
    setSyncProgress({ current: 0, total: 0, status: 'Voorbereiden...', progress_percent: 0 });
    
    try {
      const syncData = {
        dagen_terug: 30, // Last 3 months
        gemeente: selectedGemeente || undefined,
        batch_size: 25,
        page_number: pageNumber,
        test_mode: testMode // Use test mode toggle
      };

      // Use streaming sync endpoint
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/mijnperceel/sync-projects-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(syncData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.status === 'starting') {
                setSyncProgress({ 
                  current: 0, 
                  total: 0, 
                  status: data.message,
                  progress_percent: 0
                });
              } else if (data.status === 'running') {
                setSyncProgress({ 
                  current: data.current_page, 
                  total: data.total_pages, 
                  status: data.message,
                  progress_percent: data.progress_percent,
                  new_projects: data.new_projects,
                  updated_projects: data.updated_projects
                });
              } else if (data.status === 'completed') {
                setSyncProgress({ 
                  current: data.current_page, 
                  total: data.total_pages, 
                  status: data.message,
                  progress_percent: 100,
                  new_projects: data.new_projects,
                  updated_projects: data.updated_projects
                });
                
                // Refresh projects list
                await fetchMijnperceelProjects();
                
                // Show completion message immediately
                setSyncProgress(null);
                alert(`Synchronisatie succesvol!\n` +
                      `Nieuwe projecten: ${data.new_projects}\n` +
                      `Bijgewerkte projecten: ${data.updated_projects}\n` +
                      `Pagina's verwerkt: ${data.current_page}/${data.total_pages}`);
              } else if (data.status === 'error') {
                setSyncProgress(null);
                alert('Synchronisatie mislukt: ' + (data.error || data.message));
              }
            } catch (parseError) {
              console.error('Error parsing SSE data:', parseError);
            }
          }
        }
      }
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncProgress(null);
      alert('Synchronisatie mislukt: ' + (error.message || 'Onbekende fout'));
    } finally {
      setSyncing(false);
    }
  };

  const handleLoadMore = async () => {
    if (paginationInfo && paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
      await handleSync(paginationInfo.pageNumber + 1);
    }
  };

  const handleConvertToLeads = async (selectedProjects) => {
    try {
      // Convert each project individually using the new API
      const conversions = [];
      for (const project of selectedProjects) {
        const response = await axios.post(`${API}/mijnperceel/convert-to-lead`, {
          mijnperceel_project_id: project.id,
          create_lead: true
        });
        
        if (response.data.success) {
          conversions.push({
            project: project,
            conversion_id: response.data.conversion_id,
            lead_id: response.data.lead_id
          });
        }
      }
      
      if (conversions.length > 0) {
        alert(`${conversions.length} projecten zijn omgezet naar leads!`);
        await fetchMijnperceelProjects(); // Refresh to show conversion status
      }
    } catch (error) {
      console.error('Conversion failed:', error);
      alert('Conversie naar leads mislukt.');
    }
  };

  const handleRemoveConversion = async (projectId) => {
    try {
      // First get the conversions for this project to find the user's conversion
      const conversionsResponse = await axios.get(`${API}/mijnperceel/projects/${projectId}/conversions`);
      
      if (conversionsResponse.data.success && conversionsResponse.data.is_converted_by_user) {
        // Find the user's conversion
        const userConversion = conversionsResponse.data.conversions.find(conv => 
          conv.user_id === user?.id // Assuming user object is available
        );
        
        if (userConversion) {
          const response = await axios.delete(`${API}/mijnperceel/convert-to-lead/${userConversion.id}`);
          
          if (response.data.success) {
            alert('Project conversie ongedaan gemaakt!');
            await fetchMijnperceelProjects(); // Refresh to show updated status
          }
        }
      }
    } catch (error) {
      console.error('Remove conversion failed:', error);
      alert('Conversie ongedaan maken mislukt.');
    }
  };

  const handleDistributeToAllUsers = async () => {
    if (!window.confirm('Weet je zeker dat je alle projecten wilt distribueren naar gebruikers op basis van hun toegangsrechten?\n\nDit kan even duren...')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/mijnperceel/distribute-projects`);
      
      if (response.data.success) {
        const summary = response.data.summary;
        const message = `✅ Distributie voltooid!\n\n` +
          `📊 Totaal:\n` +
          `  - ${summary.total_projects_distributed} projecten gedistribueerd\n` +
          `  - ${summary.total_users_processed} gebruikers verwerkt\n\n` +
          `👥 Details per gebruiker:\n` +
          summary.user_details.slice(0, 5).map(u => 
            `  - ${u.user_name}: ${u.projects_converted || 0} project(en)`
          ).join('\n') +
          (summary.user_details.length > 5 ? `\n  ... en ${summary.user_details.length - 5} andere gebruikers` : '');
        
        alert(message);
        await fetchMijnperceelProjects();
      }
    } catch (error) {
      console.error('Distribution failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Onbekende fout';
      alert(`❌ Distributie mislukt:\n${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // Filter projects
  const filteredProjects = mijnperceelProjects.filter(project => {
    let matches = true;
    
    if (searchTerm) {
      matches = matches && (
        (project.project_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.project_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.formatted_address || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedGemeente && selectedGemeente !== 'all') {
      matches = matches && project.municipality === selectedGemeente;
    }
    
    return matches;
  });

  const uniqueGemeenten = [...new Set(mijnperceelProjects.map(p => p.municipality).filter(Boolean))].sort();

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader 
        user={user} 
        currentPage="mijnperceel"
        onNavigate={(path) => window.location.href = path}
        onLogout={logout}
      />
      
      {/* Connection Status Bar */}
      <div className="bg-slate-100 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center justify-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' : 
                connectionStatus === 'error' ? 'bg-red-500' : 'bg-yellow-500'
              }`}></div>
              <span className="text-sm text-gray-600">
                MijnPerceel API: {connectionStatus === 'connected' ? 'Verbonden' : 
                 connectionStatus === 'error' ? 'Verbinding mislukt' : 'Verbinding testen...'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">MijnPerceel Integratie</h2>
          <p className="text-gray-600">Synchroniseer omgevingsvergunningen uit MijnPerceel</p>
        </div>

        {/* Progress Indicator */}
        {syncProgress && (
          <div className="mb-6">
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-center space-x-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-medium">{syncProgress.status}</span>
                      <span className="text-blue-600 font-semibold">
                        {syncProgress.current}/{syncProgress.total} pagina's ({syncProgress.progress_percent || 0}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-blue-600 h-3 rounded-full transition-all duration-500 ease-out" 
                        style={{ width: `${syncProgress.progress_percent || 0}%` }}
                      ></div>
                    </div>
                    {(syncProgress.new_projects !== undefined || syncProgress.updated_projects !== undefined) && (
                      <div className="flex justify-between text-xs text-gray-600 mt-2">
                        <span>Nieuwe: {syncProgress.new_projects || 0}</span>
                        <span>Bijgewerkt: {syncProgress.updated_projects || 0}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">MijnPerceel projecten</p>
                  <p className="text-2xl font-bold text-gray-900">{mijnperceelProjects.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Laatste sync</p>
                  <p className="text-sm text-gray-900">
                    {mijnperceelProjects.length > 0 && mijnperceelProjects[0]?.synced_at
                      ? new Date(mijnperceelProjects[0].synced_at).toLocaleString('nl-BE', {
                          day: '2-digit',
                          month: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'Nog geen sync'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Users className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Omgezet naar leads</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {mijnperceelProjects.filter(p => p.is_converted_to_lead).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <MapPin className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Gemeenten</p>
                  <p className="text-2xl font-bold text-gray-900">{uniqueGemeenten.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Control Panel */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex justify-between items-start mb-6">
              <div className="flex flex-wrap gap-4 items-center">
                <Button
                  onClick={() => handleSync(0)}
                  disabled={syncing || connectionStatus === 'error'}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  {syncing ? 'Synchroniseren...' : 'MijnPerceel Sync'}
                </Button>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="testMode"
                    checked={testMode}
                    onChange={(e) => setTestMode(e.target.checked)}
                    disabled={syncing}
                    className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <label htmlFor="testMode" className="text-sm text-gray-600">
                    Test modus (max 10 pagina's)
                  </label>
                </div>
                
                {paginationInfo && paginationInfo.pageNumber < paginationInfo.totalPages - 1 && (
                  <Button
                    variant="outline"
                    onClick={handleLoadMore}
                    disabled={syncing}
                    className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Laad volgende 25 dossiers
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  onClick={() => handleConvertToLeads(filteredProjects.filter(p => !p.is_converted_to_lead))}
                  disabled={filteredProjects.filter(p => !p.is_converted_to_lead).length === 0}
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Converteer naar Leads ({filteredProjects.filter(p => !p.is_converted_to_lead).length})
                </Button>

                {user?.role === 'super_admin' && (
                  <Button
                    variant="default"
                    onClick={handleDistributeToAllUsers}
                    disabled={loading || mijnperceelProjects.length === 0}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Distribueer naar Alle Gebruikers
                  </Button>
                )}
              </div>
            </div>

            {/* Pagination Info */}
            {paginationInfo && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Synchronisatie info:</span> 
                  Pagina {paginationInfo.pageNumber + 1} van {paginationInfo.totalPages} 
                  ({paginationInfo.totalRecords} totaal, {paginationInfo.pageSize} per pagina)
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-64">
                <Input
                  type="text"
                  placeholder="Zoek projecten..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div className="min-w-48">
                <select
                  value={selectedGemeente}
                  onChange={(e) => setSelectedGemeente(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Alle gemeenten</option>
                  <option value="all">Alle gemeenten</option>
                  {uniqueGemeenten.map(gemeente => (
                    <option key={gemeente} value={gemeente}>{gemeente}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Projects List */}
        <Card>
          <CardHeader>
            <CardTitle>
              MijnPerceel Projecten ({filteredProjects.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Projecten laden...</div>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500">
                  {mijnperceelProjects.length === 0 
                    ? 'Geen projecten gevonden. Klik op "MijnPerceel Sync" om data op te halen.' 
                    : 'Geen projecten voldoen aan de filters.'
                  }
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredProjects.map((project) => (
                  <div
                    key={project.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-emerald-300 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg text-gray-900">
                          {project.project_name || project.project_number}
                        </h3>
                        <Badge variant={project.is_converted_to_lead ? 'default' : 'secondary'}>
                          {project.is_converted_to_lead ? 'Lead' : 'Project'}
                        </Badge>
                        {project.status && (
                          <Badge variant="outline" className="text-xs">
                            {project.status}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <div className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          <span>{project.project_number}</span>
                        </div>
                        {project.municipality && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{project.municipality}</span>
                          </div>
                        )}
                        {project.treating_authority && (
                          <div className="flex items-center gap-1">
                            <Building className="w-4 h-4" />
                            <span>{project.treating_authority}</span>
                          </div>
                        )}
                        {project.synced_at && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(project.synced_at).toLocaleDateString('nl-NL')}</span>
                          </div>
                        )}
                      </div>
                      
                      {project.formatted_address && (
                        <p className="text-sm text-gray-700">
                          {project.formatted_address}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {!project.is_converted_to_lead && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleConvertToLeads([project])}
                        >
                          <ArrowRight className="w-4 h-4 mr-1" />
                          Naar Lead
                        </Button>
                      )}
                      {project.is_converted_to_lead && (
                        <>
                          <Badge variant="default" className="bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Omgezet
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveConversion(project.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Ongedaan
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [summary, setSummary] = useState({});
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const dashboardRequestRef = useRef(0);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedZipcodes, setSelectedZipcodes] = useState([]);  // Multi-select zipcodes
  const [selectedCategories, setSelectedCategories] = useState([]);  // Multi-select categories
  const [selectedDays, setSelectedDays] = useState('15');  // Date range filter (15/30/90/all)
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [postbirdStatus, setPostbirdStatus] = useState('all');  // Renamed from contactStatus
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [zipDropdownOpenDashboard, setZipDropdownOpenDashboard] = useState(false);
  const [zipSearchDashboard, setZipSearchDashboard] = useState('');
  const [filteredZipcodesDashboard, setFilteredZipcodesDashboard] = useState([]);
  const [activeStatsTab, setActiveStatsTab] = useState(null);  // Track which stats card is active: 'total', 'campagne', 'favorieten', 'verzonden'
  
  // User access permissions for filters
  const [accessibleZipcodes, setAccessibleZipcodes] = useState([]);
  const [zipLabelsDashboard, setZipLabelsDashboard] = useState({});
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  
  const { user, logout } = useAuth();

  useEffect(() => {
    fetchSummary();
    fetchUserAccessPermissions();
    fetchLeadCategoryGroups().then(setCategoryGroups);
    let isMounted = true;
    const loadCities = async () => {
      setCitiesLoading(true);
      try {
        const options = await fetchCityOptionsFromApi();
        if (isMounted) {
          setCityOptions(options);
        }
      } catch (error) {
        console.error('Failed to load city options:', error);
      } finally {
        if (isMounted) {
          setCitiesLoading(false);
        }
      }
    };
    loadCities();
    return () => {
      isMounted = false;
    };
  }, []);

  const appendCategoryParams = (params) => {
    if (selectedCategories && selectedCategories.length > 0) {
      selectedCategories.forEach((c) => params.append('categories', c));
    }
    if (selectedDays && selectedDays !== 'all') params.set('days_back', selectedDays);
  };

  const buildDashboardLeadParams = () => {
    const params = new URLSearchParams();

    if (activeStatsTab === 'total') {
      if (selectedZipcodes && selectedZipcodes.length > 0) {
        selectedZipcodes.forEach((z) => params.append('zipcodes', z));
      }
      appendCategoryParams(params);
      if (postbirdStatus && postbirdStatus !== 'all') params.set('contact_status', postbirdStatus);
    } else if (activeStatsTab === 'campagne') {
      params.set('favorites_only', 'true');
      if (selectedZipcodes && selectedZipcodes.length > 0) {
        selectedZipcodes.forEach((z) => params.append('zipcodes', z));
      }
      appendCategoryParams(params);
      if (postbirdStatus && postbirdStatus !== 'all') params.set('contact_status', postbirdStatus);
    } else if (activeStatsTab === 'favorieten') {
      params.set('status', 'interessant');
      if (selectedZipcodes && selectedZipcodes.length > 0) {
        selectedZipcodes.forEach((z) => params.append('zipcodes', z));
      }
      appendCategoryParams(params);
      if (postbirdStatus && postbirdStatus !== 'all') params.set('contact_status', postbirdStatus);
    } else if (activeStatsTab === 'verzonden') {
      params.set('status', 'gecontacteerd');
      if (selectedZipcodes && selectedZipcodes.length > 0) {
        selectedZipcodes.forEach((z) => params.append('zipcodes', z));
      }
      appendCategoryParams(params);
    } else {
      if (selectedZipcodes && selectedZipcodes.length > 0) {
        selectedZipcodes.forEach((z) => params.append('zipcodes', z));
      }
      appendCategoryParams(params);
      if (selectedStatus && selectedStatus !== 'all') params.set('status', selectedStatus);
      if (postbirdStatus && postbirdStatus !== 'all') params.set('contact_status', postbirdStatus);
      if (showFavoritesOnly) params.set('favorites_only', 'true');
    }

    if (searchTerm) params.set('search', searchTerm);
    return params;
  };

  const hasActiveDashboardFilters = () => {
    if (activeStatsTab) return true;
    if (selectedZipcodes.length > 0) return true;
    if (selectedCategories.length > 0) return true;
    if (selectedDays !== 'all') return true;
    if (selectedStatus !== 'all') return true;
    if (postbirdStatus !== 'all') return true;
    if (searchTerm.trim()) return true;
    if (showFavoritesOnly) return true;
    return false;
  };

  const getDashboardFilterSnapshot = () => JSON.stringify({
    activeStatsTab,
    selectedZipcodes: [...selectedZipcodes].sort(),
    selectedCategories: [...selectedCategories].sort(),
    selectedDays,
    selectedStatus,
    postbirdStatus,
    searchTerm,
    showFavoritesOnly,
    pageSize,
  });

  const isDashboardRequestCurrent = (requestId, snapshot) => (
    requestId === dashboardRequestRef.current
    && getDashboardFilterSnapshot() === snapshot
  );

  const fetchFilteredCount = async (filterParams) => {
    try {
      const countResponse = await axios.get(`${API}/leads/stats/count?${filterParams.toString()}`);
      return countResponse.data?.total ?? 0;
    } catch (countError) {
      console.warn('Filtered lead count unavailable:', countError);
      return null;
    }
  };

  const fetchTotalByPaging = async (filterParams) => {
    let total = 0;
    let skip = 0;
    const batchSize = 200;
    while (true) {
      const params = new URLSearchParams(filterParams);
      params.set('skip', String(skip));
      params.set('limit', String(batchSize));
      const response = await axios.get(`${API}/leads?${params.toString()}`);
      const batch = response.data || [];
      total += batch.length;
      if (batch.length < batchSize) break;
      skip += batchSize;
    }
    return total;
  };

  const resolveFilteredTotal = async (filterParams) => {
    const apiCount = await fetchFilteredCount(filterParams);
    if (apiCount !== null) {
      return apiCount;
    }
    // Count API unavailable — verify by paging through all matches
    return fetchTotalByPaging(filterParams);
  };

  const fetchDashboardTotal = async () => {
    if (!hasActiveDashboardFilters()) {
      const response = await axios.get(`${API}/leads/stats/summary`);
      return response.data?.total_leads ?? 0;
    }
    const filterParams = buildDashboardLeadParams();
    return resolveFilteredTotal(filterParams);
  };

  const reloadDashboardView = async (pageArg, requestId, snapshot, { refreshCount = true } = {}) => {
    setLoading(true);
    try {
      const filterParams = buildDashboardLeadParams();
      const listParams = new URLSearchParams(filterParams);
      listParams.set('skip', String((pageArg - 1) * pageSize));
      listParams.set('limit', String(pageSize));

      const leadsResponse = await axios.get(`${API}/leads?${listParams.toString()}`);
      if (!isDashboardRequestCurrent(requestId, snapshot)) return;

      setLeads(leadsResponse.data || []);

      if (refreshCount) {
        const count = await fetchDashboardTotal();
        if (!isDashboardRequestCurrent(requestId, snapshot)) return;
        setFilteredTotal(count);
      }
    } catch (error) {
      console.error('Failed to reload dashboard view:', error);
    } finally {
      if (requestId === dashboardRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const refreshLeadsTotal = async () => {
    const requestId = ++dashboardRequestRef.current;
    const snapshot = getDashboardFilterSnapshot();
    try {
      const count = await fetchDashboardTotal();
      if (!isDashboardRequestCurrent(requestId, snapshot)) return;
      setFilteredTotal(count);
    } catch (error) {
      console.error('Failed to refresh lead total:', error);
    }
  };

  const fetchLeads = async (pageArg = page, pageSizeArg = pageSize) => {
    const requestId = ++dashboardRequestRef.current;
    const snapshot = getDashboardFilterSnapshot();
    await reloadDashboardView(pageArg, requestId, snapshot, { refreshCount: false });
  };

  useEffect(() => {
    setPage(1);
    const requestId = ++dashboardRequestRef.current;
    const snapshot = getDashboardFilterSnapshot();
    reloadDashboardView(1, requestId, snapshot, { refreshCount: true });
    return () => {
      dashboardRequestRef.current += 1;
    };
  }, [searchTerm, selectedZipcodes, selectedCategories, selectedDays, selectedStatus, postbirdStatus, showFavoritesOnly, activeStatsTab, pageSize]);

  useEffect(() => {
    if (page === 1) return;
    const requestId = ++dashboardRequestRef.current;
    const snapshot = getDashboardFilterSnapshot();
    reloadDashboardView(page, requestId, snapshot, { refreshCount: false });
    return () => {
      dashboardRequestRef.current += 1;
    };
  }, [page]);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/leads/stats/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const fetchUserAccessPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch user's accessible zipcodes and categories
      const response = await axios.get(`${API}/location/users/${user.id}/access-summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('User access summary:', response.data);
      
      setAccessibleZipcodes(response.data.accessible_zipcodes || []);
      setZipLabelsDashboard(response.data.zipcode_labels || {});
      
    } catch (error) {
      console.error('Failed to fetch user access permissions:', error);
      // If error (e.g., user is admin with no restrictions), allow all
      setAccessibleZipcodes([]);
    }
  };


  const markLeadAsRead = async (lead) => {
    if (!lead || lead.status === 'gelezen' || lead.status === 'interessant') {
      return;
    }
    try {
      await axios.patch(`${API}/leads/${lead.id}`, { status: 'gelezen' });
      setLeads((prevLeads) =>
        prevLeads.map((item) =>
          item.id === lead.id ? { ...item, status: 'gelezen' } : item,
        ),
      );
      setSelectedLead((prev) =>
        prev && prev.id === lead.id ? { ...prev, status: 'gelezen' } : prev,
      );
      fetchSummary();
      if (selectedStatus === 'new' && !activeStatsTab) {
        fetchLeads();
      }
    } catch (error) {
      console.error('Failed to mark lead as gelezen:', error);
    }
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setSheetOpen(true);
    markLeadAsRead(lead);
  };

  const handleLeadUpdate = () => {
    const requestId = ++dashboardRequestRef.current;
    const snapshot = getDashboardFilterSnapshot();
    reloadDashboardView(page, requestId, snapshot, { refreshCount: true });
    fetchSummary();
    setSheetOpen(false);
  };

  const handleToggleFavorite = async (leadId, isFavorite) => {
    try {
      // Update lead to favorite and set status to interessant if becoming favorite
      const updateData = {
        is_favorite: isFavorite,
        //...(isFavorite && { status: 'interessant' })
      };

      await axios.patch(`${API}/leads/${leadId}`, updateData);
      
      // Update local state
      setLeads(prevLeads => 
        prevLeads.map(lead => 
          lead.id === leadId 
            ? { 
                ...lead, 
                is_favorite: isFavorite,
                //...(isFavorite && { status: 'interessant' })
              }
            : lead
        )
      );
      
      // Refresh summary and filtered list/counts
      fetchSummary();
      const requestId = ++dashboardRequestRef.current;
      const snapshot = getDashboardFilterSnapshot();
      reloadDashboardView(page, requestId, snapshot, { refreshCount: true });
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const handleToggleStatus = async (leadId, isInteresant) => {
    try {
      // Update lead to favorite and set status to interessant if becoming favorite
      const updateData = {
        //is_favorite: isFavorite,
        ...(!isInteresant ? { status: 'interessant' } : { status: 'new' })
      };

      await axios.patch(`${API}/leads/${leadId}`, updateData);
      
      // Update local state
      setLeads(prevLeads => 
        prevLeads.map(lead => 
          lead.id === leadId 
            ? { 
                ...lead, 
                //is_favorite: isFavorite,
                ...(!isInteresant ? { status: 'interessant' } : { status: 'new' })
              }
            : lead
        )
      );
      
      // Refresh summary and filtered list/counts
      fetchSummary();
      const requestId = ++dashboardRequestRef.current;
      const snapshot = getDashboardFilterSnapshot();
      reloadDashboardView(page, requestId, snapshot, { refreshCount: true });
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const handleToggleGelezen = async (leadId,lead) => {
    const isInteresant = lead.status=='interessant' || false;
    try {
      // Update lead to favorite and set status to interessant if becoming favorite
      if(!isInteresant) {
        const updateData = {   
          ...( { status: 'gelezen' } )
        };

        await axios.patch(`${API}/leads/${leadId}`, updateData);
        
        // Update local state
        setLeads(prevLeads => 
          prevLeads.map(lead => 
            lead.id === leadId 
              ? { 
                  ...lead,               
                  ...({ status: 'gelezen' })
                }
              : lead
          )
        );
      }
      
      // Refresh summary to update counts
      fetchSummary();
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  // Build zipcode options from city API and user access summary
  const uniqueAccessibleZipcodes = accessibleZipcodes.length > 0
    ? [...new Set(accessibleZipcodes)]
    : [];

  const fallbackZipcodesDashboard = uniqueAccessibleZipcodes.length > 0
    ? uniqueAccessibleZipcodes.map(zip => ({ 
        zipcode: zip, 
        gemeente: zipLabelsDashboard[zip] || 'Onbekend',
        searchValue: buildSearchValue(zip, zipLabelsDashboard[zip] || 'Onbekend')
      }))
    : [];
  
  const availableZipcodes = useMemo(() => {
    const dbOptions = filterCityOptionsByAccess(cityOptions, accessibleZipcodes);
    if (dbOptions.length > 0) {
      return dbOptions;
    }
    return sortZipOptions(fallbackZipcodesDashboard);
  }, [cityOptions, accessibleZipcodes, fallbackZipcodesDashboard]);

  useEffect(() => {
    const searchQueryDashboard = normalizeSearchValue(zipSearchDashboard);
    if (!searchQueryDashboard) {
      setFilteredZipcodesDashboard(availableZipcodes);
      return;
    }
    const next = availableZipcodes.filter(({ zipcode, gemeente, searchValue }) => {
      const target = searchValue ?? buildSearchValue(zipcode, gemeente);
      return target.includes(searchQueryDashboard);
    });
    setFilteredZipcodesDashboard(next);
  }, [zipSearchDashboard, availableZipcodes]);
  
  const leadsTableTotal = filteredTotal;

  const totalPages = Math.max(1, Math.ceil(leadsTableTotal / pageSize));
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const listStart = leadsTableTotal === 0 ? 0 : (page - 1) * pageSize + 1;
  const listEnd = leadsTableTotal === 0 ? 0 : Math.min(page * pageSize, leadsTableTotal);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader 
        user={user} 
        currentPage="dashboard"
        onNavigate={(path) => window.location.href = path}
        onLogout={logout}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${activeStatsTab === 'total' ? 'ring-2 ring-emerald-500' : ''}`}
            onClick={() => {
              setActiveStatsTab(activeStatsTab === 'total' ? null : 'total');
              setSelectedStatus('all');
              setShowFavoritesOnly(false);
            }}
          >
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Building className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Totaal leads</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.total_leads || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${activeStatsTab === 'campagne' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => {
              setActiveStatsTab(activeStatsTab === 'campagne' ? null : 'campagne');
              setSelectedStatus('all');
              setShowFavoritesOnly(false);
            }}
          >
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Mail className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Campagne</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.favorites_count || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${activeStatsTab === 'favorieten' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => {
              setActiveStatsTab(activeStatsTab === 'favorieten' ? null : 'favorieten');
              setShowFavoritesOnly(false);
              setSelectedStatus('interessant');
            }}
          >
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Star className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Favorieten</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.status_counts?.interessant || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${activeStatsTab === 'verzonden' ? 'ring-2 ring-purple-500' : ''}`}
            onClick={() => {
              setActiveStatsTab(activeStatsTab === 'verzonden' ? null : 'verzonden');
              setShowFavoritesOnly(false);
              setSelectedStatus('gecontacteerd');
            }}
          >
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Send className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Verzonden Campagnes</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.campaigns_count || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

              <div className="space-y-2 relative">
                <Label>Postcodes</Label>
                <button
                  type="button"
                  onClick={() => setZipDropdownOpenDashboard(!zipDropdownOpenDashboard)}
                  className="w-full px-3 py-2 h-12 text-base border border-gray-300 rounded-md text-left focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {selectedZipcodes.length === 0 ? 'Alle postcodes' : `${selectedZipcodes.length} geselecteerd`}
                </button>
                {zipDropdownOpenDashboard && (
                  <div className="absolute z-50 mt-2 w-full max-h-64 overflow-auto bg-white border border-gray-200 rounded-md shadow">
                    <div className="p-2">
                      <div className="mb-2">
                        <Input
                          type="text"
                          placeholder="Zoek postcode of gemeente..."
                          value={zipSearchDashboard}
                          onChange={(e) => setZipSearchDashboard(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      <div className="flex items-center gap-2 px-2 py-1">
                        <input
                          id="zip-all-dashboard"
                          type="checkbox"
                          checked={filteredZipcodesDashboard.length > 0 && filteredZipcodesDashboard.every(z => selectedZipcodes.includes(z.zipcode))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedZipcodes(prev => [...new Set([...prev, ...filteredZipcodesDashboard.map(z => z.zipcode)])]);
                            } else {
                              setSelectedZipcodes(prev => prev.filter(z => !filteredZipcodesDashboard.some(f => f.zipcode === z)));
                            }
                          }}
                        />
                        <label htmlFor="zip-all-dashboard" className="text-sm text-gray-700">Alles selecteren</label>
                      </div>
                      <div className="border-t my-1" />
                      {filteredZipcodesDashboard.length === 0 && (
                        <div className="px-2 py-1 text-sm text-gray-500">
                          {citiesLoading ? 'Postcodes laden...' : 'Geen postcodes gevonden'}
                        </div>
                      )}
                      {filteredZipcodesDashboard.map(({ zipcode, gemeente }, index) => (
                        <label key={`${zipcode}-${index}`} className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={selectedZipcodes.includes(zipcode)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedZipcodes(prev => [...new Set([...prev, zipcode])]);
                              } else {
                                setSelectedZipcodes(prev => prev.filter(z => z !== zipcode));
                              }
                            }}
                          />
                          <span className="text-sm text-gray-700">{gemeente} ({zipcode})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Periode (aanvraagdatum)</Label>
                <Select value={selectedDays} onValueChange={setSelectedDays}>
                  <SelectTrigger className="h-12 text-base">
                    <SelectValue placeholder="Laatste 15 dagen" />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_RANGE_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Categorie</Label>
                <CategoryMultiSelect
                  groups={categoryGroups}
                  selected={selectedCategories}
                  onChange={setSelectedCategories}
                />
                {categoryGroups.length === 0 && (
                  <p className="text-xs text-gray-500">Geen categorierechten toegewezen</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Lead Status</Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="h-12 text-base">
                    <SelectValue placeholder="Alle statussen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle statussen</SelectItem>
                    <SelectItem value="new">Nieuw</SelectItem>
                    <SelectItem value="interessant">Favorieten</SelectItem>
                    <SelectItem value="gelezen">Gelezen</SelectItem>                    
                    <SelectItem value="gecontacteerd">Gecontacteerd</SelectItem>
                    {/*<SelectItem value="lopend">Lopend</SelectItem>
                    <SelectItem value="offerte">Offerte</SelectItem>
                    <SelectItem value="gearchiveerd">Gearchiveerd</SelectItem>*/}
                  </SelectContent>
                </Select>
              </div>

              {/*<div className="space-y-2">
                <Label>Postbird Status</Label>
                <Select value={postbirdStatus} onValueChange={setPostbirdStatus}>
                  <SelectTrigger className="h-12 text-base">
                    <SelectValue placeholder="Alle statussen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="contacted">Verstuurd via Postbird</SelectItem>
                    <SelectItem value="not_contacted">Nog niet verstuurd</SelectItem>
                  </SelectContent>
                </Select>
              </div>*/}

              {/* Full-width search at the bottom of filters */}
              <div className="space-y-2 lg:col-span-4">
                <Label>Zoeken</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Zoek in titel, beschrijving..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-12 text-base"
                  />
                </div>
              </div>

             {/* <div className="space-y-2">
                <Label>Filters</Label>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="favorites"
                    checked={showFavoritesOnly}
                    onCheckedChange={setShowFavoritesOnly}
                  />
                  <Label htmlFor="favorites" className="text-sm">
                    Alleen favorieten
                  </Label>
                </div>
              </div>*/}
            </div>
          </CardContent>
        </Card>

        {/* Leads List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                Leads ({leadsTableTotal})
              </CardTitle>
              {leadsTableTotal > 0 && (
                <div className="text-sm text-gray-600 flex items-center gap-4">
                  <span>
                    Toont {listStart}–{listEnd} van {leadsTableTotal}
                  </span>
                  <span>{pageSize} / pagina</span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Leads laden...</div>
              </div>
            ) : leads.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Geen leads gevonden</div>
              </div>
            ) : (
              <div className="space-y-4">
                {leads.map((lead) => (
                  <div
                    key={lead.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer relative"
                    onClick={() => {
                      handleLeadClick(lead);
                      handleToggleGelezen(lead.id,lead)
                    }}
                  >    
                    {/* Favorite Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`absolute top-3 right-3 p-1 h-8 w-8 rounded-full hover:bg-gray-100 ${lead.is_favorite ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFavorite(lead.id, !lead.is_favorite);
                      }}
                    >
                      <Mail className={`w-5 h-5 ${lead.is_favorite ? 'text-red-500 bg-red-50 rounded p-0.5' : 'text-gray-400'}`} style={{ width: '30px', height: '30px' }} />
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm" style={{ marginTop: '35px' }}
                      className={`absolute top-3 right-3 p-1 h-8 w-8 rounded-full hover:bg-gray-100 ${lead.status == 'interessant' ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStatus(lead.id, lead.status == 'interessant');
                      }}
                    >
                      <Star className={`w-5 h-5 ${lead.status == 'interessant' ? 'text-yellow-500 bg-red-50 rounded p-0.5' : 'text-gray-400'}`} style={{ width: '30px', height: '30px' }} />
                    </Button>             
                    <div className="flex items-start space-x-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg text-gray-900">
                            {lead.title}
                          </h3>
                          <StatusBadge status={lead.status} />
                          {lead.postbird_id && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              <MailOpen className="w-3 h-3 mr-1" />
                              Gecontacteerd
                            </Badge>
                          )}
                          {lead.is_favorite && (
                            <Mail className="w-4 h-4 text-red-500 bg-red-50 rounded p-0.5" />
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-2">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{lead?.street} {lead?.number}, {lead?.zipcode} {lead?.municipality}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Building className="w-4 h-4" />
                            <span>{lead.permit_type}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(lead.datum_aanvraag).toLocaleDateString('nl-NL')}</span>
                          </div>
                        </div>
                        
                        {lead.eigenaar_info && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-2">
                            {lead.eigenaar_info.name && (
                              <div className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                <span>{lead.eigenaar_info.name}</span>
                              </div>
                            )}
                            {lead.eigenaar_info.email && (
                              <div className="flex items-center gap-1">
                                <Mail className="w-4 h-4" />
                                <span>{lead.eigenaar_info.email}</span>
                              </div>
                            )}
                            {lead.eigenaar_info.phone && (
                              <div className="flex items-center gap-1">
                                <Phone className="w-4 h-4" />
                                <span>{lead.eigenaar_info.phone}</span>
                              </div>
                            )}
                            {lead.eigenaar_info.address && lead.eigenaar_info.address !== ", " && (
                              <div className="flex items-center gap-2 text-sm">
                                <MapPin className="w-4 h-4" />
                                <span>{lead.eigenaar_info.address}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {lead.description}
                        </p>
                      </div>
                      

                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-gray-600">
            {leadsTableTotal
              ? `Toont ${listStart}–${listEnd} van ${leadsTableTotal}`
              : `Pagina ${page}`}
          </div>

          <div className="flex items-center gap-2">
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[110px]">
                <SelectValue placeholder={`${pageSize} / pagina`} />
              </SelectTrigger>
              <SelectContent>
                {[25, 50, 100].map(s => (
                  <SelectItem key={s} value={String(s)}>{s} / pagina</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" size="sm" onClick={() => setPage(1)} disabled={!canPrev}>
              « Eerste
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={!canPrev}>
              ‹ Vorige
            </Button>
            <span className="text-sm text-gray-700 px-2">
              Pagina {page}{leadsTableTotal ? ` van ${totalPages}` : ''}
            </span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={!canNext}>
              Volgende ›
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(totalPages)} disabled={!canNext}>
              Laatste »
            </Button>
          </div>
        </div>
      </div>

      {/* Lead Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <LeadDetailSheet
          lead={selectedLead}
          onClose={() => setSheetOpen(false)}
          onUpdate={handleLeadUpdate}
          fetchLeads={fetchLeads}
          fetchSummary={fetchSummary}
        />
      </Sheet>
    </div>
  );
};

// Profile Component
const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const location = useLocation();
  
  // Password change state
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // Subscription state
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [reactivatingSubscription, setReactivatingSubscription] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [subscriptionSuccess, setSubscriptionSuccess] = useState('');
  // Credits
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditTx, setCreditTx] = useState([]);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [buyingCredits, setBuyingCredits] = useState(false);
  const [creditsToBuy, setCreditsToBuy] = useState(10);
  const [creditItems, setCreditItems] = useState([]);
  const [selectedCreditItemId, setSelectedCreditItemId] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    company_name: '',
    company_street: '',
    company_number: '',
    company_city: '',
    company_postal_code: '',
    company_vat_number: ''
  });
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
    fetchSubscriptionInfo();
    fetchCredits();
    fetchCreditItems();
  }, []);

  // Auto-scroll to credits section when hash is present (wait until content rendered)
  useEffect(() => {
    if (location.hash === '#credits' && !loading) {
      const timer = setTimeout(() => {
        const creditsElement = document.getElementById('credits');
        if (creditsElement) {
          creditsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [location.hash, loading]);

  // Show thank-you after Mollie redirects back with ?payment=success
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      const payment = params.get('payment');
      if (payment === 'success') {
        setSuccess('Bedankt voor uw aankoop. Uw credits werden automatisch toegevoegd aan uw account.');
        // Refresh balance and transactions inline to avoid hook deps issues
        (async () => {
          try {
            const [balRes, txRes] = await Promise.all([
              axios.get(`${API}/credits/balance`),
              axios.get(`${API}/credits/transactions?limit=20`)
            ]);
            setCreditBalance(balRes.data);
            setCreditTx(txRes.data || []);
          } catch (e) {
            console.error('Failed to refresh credits after payment:', e);
          }
        })();
        // Clean the URL so the message does not keep reappearing on refresh
        navigate('/profile', { replace: true });
      }
    } catch {}
  }, [location.search, navigate]);
  const fetchSubscriptionInfo = async () => {
    setLoadingSubscription(true);
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API}/subscription/info`);
        const data = response.data || {};
        // Normalize shape to avoid null-access in UI
        setSubscriptionInfo({
          subscription: data.subscription ?? null,
          days_remaining: data.days_remaining ?? 0,
          can_cancel: data.can_cancel ?? false,
          next_renewal_date: data.next_renewal_date ?? null
        });
      }
    } catch (error) {
      console.error('Failed to fetch subscription info:', error);
      setSubscriptionInfo(null);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handleDownloadInvoice = async (t) => {
    if (!t?.payment_id) return;
    try {
      setDownloadingInvoiceId(t.id);
      // Ensure auth header is set
      const token = localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      const res = await axios.get(`${API}/payments/${t.payment_id}/invoice`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${t.payment_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to download invoice:', e);
      alert('Kon factuur niet downloaden.');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    setSubscriptionError('');
    try {
      await axios.post(`${API}/subscription/cancel`);
      setSubscriptionSuccess('Subscription cancellation requested successfully. You will receive a confirmation email shortly.');
      setShowCancelConfirm(false);
      fetchSubscriptionInfo();
    } catch (error) {
      setSubscriptionError(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleReactivateAutoRenew = async () => {
    setReactivatingSubscription(true);
    setSubscriptionError('');
    try {
      await axios.put(`${API}/subscription/reactivate-auto-renew`);
      setSubscriptionSuccess('Auto-renewal reactivated successfully.');
      fetchSubscriptionInfo();
    } catch (error) {
      setSubscriptionError(error.response?.data?.detail || 'Failed to reactivate auto-renewal');
    } finally {
      setReactivatingSubscription(false);
    }
  };

  const fetchCredits = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const [balRes, txRes] = await Promise.all([
        axios.get(`${API}/credits/balance`),
        axios.get(`${API}/credits/transactions?limit=20`)
      ]);
      setCreditBalance(balRes.data);
      setCreditTx(txRes.data || []);
    } catch (e) {
      console.error('Failed to fetch credits:', e);
    }
  };

  const handleBuyCredits = async () => {
    try {
      setBuyingCredits(true);
      const loose = creditItems.find(i => i.id === selectedCreditItemId && !i.packCredits);
      const pack = creditItems.find(i => i.id === selectedCreditItemId && i.packCredits);
      const payload = pack
        ? { itemId: selectedCreditItemId }
        : { itemId: selectedCreditItemId || null, credits: Number(creditsToBuy) || null };
      const res = await axios.post(`${API}/payments/mollie/create`, payload);
      const url = res.data?.checkoutUrl;
      if (url) {
        window.location.href = url;
      } else {
        alert('Failed to create payment');
        setBuyingCredits(false);
      }
    } catch (e) {
      console.error('Buy credits failed:', e);
      alert(e.response?.data?.detail || 'Failed to start payment');
      setBuyingCredits(false);
    }
  };

  const fetchCreditItems = async () => {
    try {
      const res = await axios.get(`${API}/payments/credit-items`);
      setCreditItems(res.data || []);
    } catch (e) {
      console.error('Failed to load credit items', e);
      setCreditItems([]);
    }
  };

  const fetchProfile = async () => {
    try {
      // Ensure token is set in axios headers
      const token = localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.get(`${API}/profile/me`);
      setProfile(response.data);
      setFormData({
        full_name: response.data.full_name || '',
        email: response.data.email || '',
        company_name: response.data.company_name || '',
        company_street: response.data.company_street || '',
        company_number: response.data.company_number || '',
        company_city: response.data.company_city || '',
        company_postal_code: response.data.company_postal_code || '',
        company_vat_number: response.data.company_vat_number || ''
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Ensure token is set in axios headers
      const token = localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.put(`${API}/profile/me`, formData);
      setProfile(response.data);
      setSuccess('Profile updated successfully!');
      
      // Update the user context with new data
      // This would require updating the AuthContext to handle profile updates
      
    } catch (error) {
      console.error('Failed to update profile:', error);
      setError(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setChangingPassword(true);
    setPasswordError('');
    setPasswordSuccess('');

    // Basic validation
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      setChangingPassword(false);
      return;
    }

    if (passwordData.new_password.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      setChangingPassword(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.post(`${API}/profile/change-password`, passwordData);
      setPasswordSuccess(response.data.message);
      
      // Clear password form
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
    } catch (error) {
      console.error('Failed to change password:', error);
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader 
        user={user} 
        currentPage="profile"
        onNavigate={(path) => navigate(path)}
        onLogout={logout}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Profiel bewerken</h2>
          <p className="text-gray-600">Beheer uw persoonlijke en bedrijfsgegevens</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-green-800">{success}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-600 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {/* Profile Form */}
        <Card>
          <CardHeader>
            <CardTitle>Profielgegevens</CardTitle>
            <CardDescription>
              Bewerk uw persoonlijke informatie en bedrijfsgegevens
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Persoonlijke gegevens</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Volledige naam</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-mailadres</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Company Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Bedrijfsgegevens</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Bedrijfsnaam</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      placeholder="Uw bedrijfsnaam"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_street">Straat</Label>
                    <Input
                      id="company_street"
                      name="company_street"
                      value={formData.company_street}
                      onChange={handleInputChange}
                      placeholder="Straatnaam"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_number">Huisnummer</Label>
                    <Input
                      id="company_number"
                      name="company_number"
                      value={formData.company_number}
                      onChange={handleInputChange}
                      placeholder="123"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_city">Stad</Label>
                    <Input
                      id="company_city"
                      name="company_city"
                      value={formData.company_city}
                      onChange={handleInputChange}
                      placeholder="Stad"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_postal_code">Postcode</Label>
                    <Input
                      id="company_postal_code"
                      name="company_postal_code"
                      value={formData.company_postal_code}
                      onChange={handleInputChange}
                      placeholder="1234 AB"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_vat_number">BTW Nummer</Label>
                    <Input
                      id="company_vat_number"
                      name="company_vat_number"
                      value={formData.company_vat_number}
                      onChange={handleInputChange}
                      placeholder="NL123456789B01"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Submit Button */}
              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(getDefaultModuleRoute(user))}
                >
                  Annuleren
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? 'Opslaan...' : 'Wijzigingen opslaan'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Password Change Card */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Wachtwoord wijzigen</CardTitle>
            <CardDescription>
              Wijzig uw wachtwoord voor extra beveiliging
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Password Success/Error Messages */}
            {passwordSuccess && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span className="text-green-800">{passwordSuccess}</span>
                </div>
              </div>
            )}

            {passwordError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <XCircle className="w-5 h-5 text-red-600 mr-2" />
                  <span className="text-red-800">{passwordError}</span>
                </div>
              </div>
            )}

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Huidig wachtwoord</Label>
                <Input
                  id="current_password"
                  name="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                  required
                  placeholder="Voer uw huidige wachtwoord in"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new_password">Nieuw wachtwoord</Label>
                <Input
                  id="new_password"
                  name="new_password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                  placeholder="Minimaal 6 tekens"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Bevestig nieuw wachtwoord</Label>
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  required
                  placeholder="Voer het nieuwe wachtwoord opnieuw in"
                />
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={changingPassword}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {changingPassword ? 'Wachtwoord wijzigen...' : 'Wachtwoord wijzigen'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Subscription Management Card */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Abonnement Beheer</CardTitle>
            <CardDescription>
              Beheer je abonnement en toegangsinstellingen
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Subscription Success/Error Messages */}
            {subscriptionSuccess && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span className="text-green-800">{subscriptionSuccess}</span>
                </div>
              </div>
            )}

            {subscriptionError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <XCircle className="w-5 h-5 text-red-600 mr-2" />
                  <span className="text-red-800">{subscriptionError}</span>
                </div>
              </div>
            )}

            {loadingSubscription ? (
              <div className="text-center py-8">
                <div className="text-lg">Loading subscription info...</div>
              </div>
            ) : subscriptionInfo?.subscription ? (
              <div className="space-y-6">
                {/* Subscription Status */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Abonnement Status</h3>
                    <Badge variant={subscriptionInfo.subscription.is_active ? "default" : "destructive"}>
                      {subscriptionInfo.subscription.is_active ? "Actief" : "Inactief"}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Startdatum:</span>
                      <div className="font-medium mt-1">
                        {new Date(subscriptionInfo.subscription.start_date).toLocaleDateString()}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Einddatum:</span>
                      <div className="font-medium mt-1">
                        {new Date(subscriptionInfo.subscription.end_date).toLocaleDateString()}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Days Remaining:</span>
                      <div className="font-medium mt-1 text-emerald-600">
                        {subscriptionInfo.days_remaining} days
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Auto Renew:</span>
                      <div className="font-medium mt-1">
                        {subscriptionInfo.subscription.auto_renew ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>

                  {subscriptionInfo.subscription.cancellation_requested && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center text-yellow-800">
                        <Clock className="w-4 h-4 mr-2" />
                        <span className="text-sm">
                          Cancellation requested. Subscription will end on {new Date(subscriptionInfo.subscription.end_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  {!subscriptionInfo.subscription.cancellation_requested && subscriptionInfo.can_cancel && (
                    <Button
                      variant="destructive"
                      onClick={() => setShowCancelConfirm(true)}
                      className="flex items-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Abonnement Opzeggen
                    </Button>
                  )}

                  {subscriptionInfo.subscription.cancellation_requested && (
                    <Button
                      onClick={handleReactivateAutoRenew}
                      disabled={reactivatingSubscription}
                      className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
                    >
                      <RefreshCw className={`w-4 h-4 ${reactivatingSubscription ? 'animate-spin' : ''}`} />
                      {reactivatingSubscription ? 'Opnieuw Activeren...' : 'Auto-verlenging Opnieuw Activeren'}
                    </Button>
                  )}

                  {!subscriptionInfo.can_cancel && !subscriptionInfo.subscription.cancellation_requested && (
                    <div className="text-sm text-yellow-600 flex items-center bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                      <Clock className="w-4 h-4 mr-2" />
                      Opzegging is niet toegestaan binnen de laatste 90 dagen. Je kunt opzeggen wanneer je meer dan 90 dagen resterend hebt.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-600">
                Geen actief abonnement gevonden
              </div>
            )}

            {/* Credits section */}
            <div id="credits" className="mt-8 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Credits</h3>
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Huidig saldo:</span>
                    <div className="font-medium mt-1">
                      {creditBalance ? creditBalance.current_balance : '...'} credits
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Transacties:</span>
                    <div className="font-medium mt-1">
                      {creditBalance ? creditBalance.transaction_count : '...'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 border rounded-lg">
                <div className="space-y-3">
                  <div>
                    <Label>Product</Label>
                    <Select value={selectedCreditItemId} onValueChange={setSelectedCreditItemId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer een pakket of losse credits" />
                      </SelectTrigger>
                      <SelectContent>
                        {creditItems.map(ci => {
                          const unitPrice = Number(ci.unitPrice || 0);
                          const perCredit = ci.packCredits ? (unitPrice && ci.packCredits ? (unitPrice / ci.packCredits) : null) : unitPrice || null;
                          const left = ci.packCredits ? `Aankoop pakket ${ci.packCredits} credits` : 'Aankoop credits';
                          const right = ci.packCredits
                            ? `€${unitPrice.toFixed(2)} (${perCredit != null ? `€${perCredit.toFixed(2)} per credit` + (ci.vatPercentage ? ` (excl. ${ci.vatPercentage*100}% BTW)` : '') : '-'})`
                            : (perCredit != null ? `€${perCredit.toFixed(2)} per credit` + (ci.vatPercentage ? ` (excl. ${ci.vatPercentage *100}% BTW)` : '') : 'prijs onbekend');
                          return (
                            <SelectItem key={ci.id} value={ci.id}>
                              {left} — {right}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  {(() => {
                    const selected = creditItems.find(c => c.id === selectedCreditItemId);
                    if (selected && !selected.packCredits) {
                      return (
                        <div className="flex items-end gap-3">
                          <div className="flex-1">
                            <Label>Aantal (1–49)</Label>
                            <Input
                              type="number"
                              min={1}
                              max={49}
                              value={creditsToBuy}
                              onChange={(e) => setCreditsToBuy(e.target.value)}
                              placeholder="Aantal credits"
                            />
                            <div className="text-xs text-gray-600 mt-1">
                              Totaal: €{(Number(creditsToBuy || 0) * Number(selected.unitPrice || 0)).toFixed(2)}
                            </div>
                          </div>
                          <Button onClick={handleBuyCredits} disabled={buyingCredits || !creditsToBuy || Number(creditsToBuy) < 1 || Number(creditsToBuy) > 49}>
                            {buyingCredits ? 'Starten...' : 'Koop met Mollie'}
                          </Button>
                        </div>
                      );
                    }
                    if (selected && selected.packCredits) {
                      return (
                        <div className="flex items-center justify-between">
                          <div className="text-sm">
                            <div>Bundel: {selected.packCredits} credits</div>
                            <div>Prijs: €{Number(selected.unitPrice || 0).toFixed(2)}</div>
                          </div>
                          <Button onClick={handleBuyCredits} disabled={buyingCredits}>
                            {buyingCredits ? 'Starten...' : 'Koop met Mollie'}
                          </Button>
                        </div>
                      );
                    }
                    return (
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">Selecteer een product om door te gaan.</div>
                        <Button onClick={handleBuyCredits} disabled>
                          Koop met Mollie
                        </Button>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="p-4 border rounded-lg">
                <div className="text-sm font-medium mb-2">Transactiegeschiedenis</div>
                {creditTx.length === 0 ? (
                  <div className="text-sm text-gray-600">Geen transacties gevonden</div>
                ) : (
                  <div className="space-y-2">
                    {creditTx.map((t) => (
                      <div key={t.id} className="flex items-center justify-between text-sm border-b pb-2">
                        <div className="flex-1 pr-3">
                          <div className="font-medium">{t.transaction_type.toUpperCase()}</div>
                          <div className="text-gray-600 break-words">{t.description}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          {t.payment_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadInvoice(t)}
                              disabled={downloadingInvoiceId === t.id}
                            >
                              {downloadingInvoiceId === t.id ? 'Downloaden...' : 'Download factuur'}
                            </Button>
                          )}
                          <div className="text-right min-w-[90px]">
                            <div className={t.transaction_type === 'debit' ? 'text-red-600' : 'text-emerald-700'}>
                              {t.transaction_type === 'debit' ? '-' : '+'}{Math.abs(t.amount)} cr
                            </div>
                            <div className="text-xs text-gray-500">{new Date(t.created_at).toLocaleString()}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cancel Confirmation Dialog */}
        <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Abonnement Opzeggen</DialogTitle>
              <DialogDescription>
                Weet je zeker dat je je abonnement wilt opzeggen?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-gray-600 mb-4">
                Je abonnement blijft actief tot {subscriptionInfo?.subscription ? new Date(subscriptionInfo.subscription.end_date).toLocaleDateString() : '-'}.
                Na die datum verlies je toegang tot het systeem.
              </p>
              <p className="text-sm text-gray-600 mb-4">
                Als je van gedachten verandert voor de einddatum, kun je automatische verlenging vanaf deze pagina opnieuw activeren.
              </p>
              <p className="text-sm text-red-600 font-medium">
                *Bij beëindigen abonnement worden eventuele niet gebruikte credits niet terugbetaald.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCancelConfirm(false)}>
                Abonnement Behouden
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription}
              >
                {cancellingSubscription ? 'Opzeggen...' : 'Ja, Abonnement Opzeggen'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Admin Route Component
const AdminRoute = () => {
  const { user, logout } = useContext(AuthContext);

  // Check if user has admin access
  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access the admin panel.</p>
        </div>
      </div>
    );
  }

  return <AdminDashboard user={user} onLogout={logout} />;
};

const SuperAdminRoute = () => {
  const { user } = useContext(AuthContext);

  // Check if user has super admin access
  if (!user || user.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Toegang Geweigerd</h1>
          <p className="text-gray-600 mb-6">Je hebt geen toegang tot deze pagina. Alleen super admins kunnen MijnPerceel synchroniseren.</p>
          <Button onClick={() => window.location.href = getDefaultModuleRoute(user)}>
            Terug naar Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return <MijnPerceelModule />;
};

// Admin Panel
const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, impersonateUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate(getDefaultModuleRoute(user));
      return;
    }
    fetchUsers();
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (userId) => {
    const result = await impersonateUser(userId);
    if (result.success) {
      navigate(getDefaultModuleRoute(result.user));
    }
  };

  if (user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-emerald-900">Admin Panel</h1>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(getDefaultModuleRoute(user))}
            >
              Terug naar Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Gebruikers</CardTitle>
            <CardDescription>
              Beheer alle gebruikers in het systeem
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">Gebruikers laden...</div>
              </div>
            ) : (
              <div className="space-y-4">
                {users.map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                  >
                    <div>
                      <h3 className="font-semibold">{u.full_name}</h3>
                      <p className="text-sm text-gray-600">{u.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                          {u.role}
                        </Badge>
                        <Badge variant={u.is_active ? 'default' : 'destructive'}>
                          {u.is_active ? 'Actief' : 'Inactief'}
                        </Badge>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleImpersonate(u.id)}
                      disabled={u.id === user.id}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Inloggen als
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, moduleId }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Laden...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!hasModuleAccess(user, moduleId)) {
    return <Navigate to={getDefaultModuleRoute(user)} replace />;
  }
  
  return children;
};

const DefaultModuleRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={getDefaultModuleRoute(user)} replace />;
};

// Main App Component
function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute moduleId="permits">
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/prospectie"
              element={
                <ProtectedRoute moduleId="permits">
                  <ProspectieModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projecten"
              element={
                <ProtectedRoute>
                  <ProjectsModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/oo-woningen"
              element={
                <ProtectedRoute moduleId="oo_woningen">
                  <OOWoningenModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vacant-spaces"
              element={
                <ProtectedRoute moduleId="vacant_spaces">
                  <VacantSpacesModule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mijnperceel"
              element={
                <ProtectedRoute>
                  <SuperAdminRoute />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/module-access"
              element={
                <ProtectedRoute>
                  <AdminModuleAccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute moduleId="admin">
                  <AdminRoute />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DefaultModuleRedirect />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
